/**
 * Product Customizer V2
 * Clean vanilla JS implementation
 */
(function() {
  'use strict';

  // ============================================
  // CONSTANTS
  // ============================================
  const MIN_FONT_SIZE = 10;    // Minimum readable font size
  const TEXT_MARGIN = 20;      // Margin around text within canvas bounds
  const DEFAULT_FONT = { id: 'arial', name: 'Arial', type: 'system', family: 'Arial, sans-serif' };

  // Switchable thread placeholder hexes — if a <g fill="..."> in a logo SVG
  // uses one of these exact values, it's a placeholder for slot N from the
  // location's threadInstructions, not a real color. Case-insensitive.
  // Anything outside this set (including real library colors like #5e3424
  // "Coffee Bean Brown") is treated as a fixed hex and matched via threadLookup.
  const SWITCHABLE_PLACEHOLDER_SLOTS = {
    '#1e4287': 0, // slot 1
    '#2ed8c4': 1, // slot 2
    '#3e6c45': 2, // slot 3
    '#4ef5aa': 3, // slot 4
    '#5e2d5a': 4  // slot 5
  };
  function getSwitchablePlaceholderSlot(fillValue) {
    if (!fillValue) return undefined;
    return SWITCHABLE_PLACEHOLDER_SLOTS[fillValue.toLowerCase()];
  }

  // ============================================
  // FEATURE SETTINGS HELPERS
  // ============================================

  /**
   * Check if a logo customizer setting is enabled
   * Settings: 'Resize', 'Recolor', 'Move'
   */
  function hasLogoSetting(setting) {
    const settings = window.CustomizerV2Data?.logoCustomizerSettings || [];
    return Array.isArray(settings) && settings.includes(setting);
  }

  /**
   * Check if a personalization setting is enabled
   * Settings: 'Show', 'Resize', 'Recolor', 'Move', 'Font', 'Align'
   */
  function hasPersonalizationSetting(setting) {
    const settings = window.CustomizerV2Data?.personalizationSettings || [];
    return Array.isArray(settings) && settings.includes(setting);
  }

  /**
   * Check if logo should be completely hidden
   * When 'Hide Logo' is in logoCustomizerSettings, only personalization is shown
   */
  function shouldHideLogo() {
    return hasLogoSetting('Hide Logo');
  }

  /**
   * Check if user should have option to hide logo per-location
   * When 'User Hide Logo' is in logoCustomizerSettings, shows checkbox
   */
  function hasUserHideLogo() {
    return hasLogoSetting('User Hide Logo');
  }

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  /**
   * Get the default font - first from personalizationFonts or fallback to Arial
   */
  function getDefaultFont() {
    const fonts = window.CustomizerV2Data?.personalizationFonts;
    if (fonts && fonts.length > 0) {
      return fonts[0];
    }
    return DEFAULT_FONT;
  }

  /**
   * Get all available personalization fonts
   */
  function getPersonalizationFonts() {
    const fonts = window.CustomizerV2Data?.personalizationFonts;
    if (fonts && fonts.length > 0) {
      return fonts;
    }
    return [DEFAULT_FONT];
  }

  /**
   * Load a font and wait for it to be ready
   * @param {Object} font - Font object with family, type properties
   * @returns {Promise<void>}
   */
  async function loadFont(font) {
    if (!font || !font.family) {
      return;
    }

    // System fonts don't need loading
    if (font.type === 'system') {
      return;
    }

    // Wait for all fonts to be ready (ensures @font-face rules are processed)
    await document.fonts.ready;

    // Check if font is already loaded
    if (document.fonts.check(`16px "${font.family}"`)) {
      return;
    }

    // Try to load the font using CSS Font Loading API
    try {
      await document.fonts.load(`16px "${font.family}"`);
      // Give browser time to make font available for rendering
      await new Promise(resolve => setTimeout(resolve, 50));
    } catch (e) {
      // Font load failed, will use fallback
    }
  }

  /**
   * Measure text bounds using Fabric.js font metrics with safety buffer
   * The safety buffer accounts for glyph overhang that Fabric doesn't report
   */
  function measureTextPixelBounds(text, fontSize, fontFamily) {
    const tempText = new fabric.Text(text, {
      fontFamily: fontFamily,
      fontSize: fontSize
    });

    // Add 30% safety buffer for glyph overhang
    const SAFETY_BUFFER = 1.30;

    return {
      width: tempText.width * SAFETY_BUFFER,
      height: tempText.height * SAFETY_BUFFER
    };
  }

  // ============================================
  // STATE
  // ============================================
  const state = {
    // Variant management
    selectedVariantId: null,
    selectedOptions: [], // Track selected value per option index: ['Red', 'Large', ...]
    variantLocationsCache: {}, // Cache: { variantId: [locations] }

    // Location management
    locations: [],
    selectedLocations: [],
    activeLocation: null,

    // View state
    activeTab: 'mockup', // 'mockup' | 'logo'
    customizeTab: 'customize', // 'customize' | 'personalize'
    activeThreadPicker: null, // Index of open thread picker, or null
    threadSearchQuery: '', // Search query for thread color picker
    threadPickerLoadedCount: 50, // Number of threads currently loaded (for lazy loading)

    // Fabric.js canvas instance
    fabricCanvas: null,

    // Per-location customization state
    locationState: {},
    // Structure:
    // [locationId]: {
    //   selectedLogo: { id, url },
    //   logoPosition: { x, y, width, height, scaleX, scaleY },
    //   logoThreadColors: {},   // Per-logo custom colors: { [logoId]: { [threadIndex]: { hex, name, code } } }
    //   svgThreadLayers: {},    // Per-logo parsed layers: { [logoId]: [{ index, defaultHex, name, code }] }
    //   noLogo: boolean,        // User opted out of logo for this location
    //   personalization: { text, threadColor: { hex, name, code }, position: { left, top, fontSize, angle } }
    // }

    // Text personalization state
    activeTextThreadPicker: false, // Whether text thread picker is open
    textThreadSearchQuery: '',     // Search query for text thread picker
    textThreadPickerLoadedCount: 50, // Lazy loading count for text thread picker

    // Font dropdown state
    fontDropdownOpen: false
  };

  // Saved state for product page (when modal takes over)
  let savedProductPageContext = null;

  // ============================================
  // SCREENSHOT SERVICE INTEGRATION
  // ============================================
  /**
   * Capture the live customizer canvas for every selected location.
   * Uses the live editor's <img> + Fabric canvas — same pixels the customer
   * approved — instead of rebuilding from saved coordinates. The active
   * location is briefly switched between captures so each one renders to the
   * live canvas first; the original active location is restored at the end.
   * @param {Object} captureState  Snapshot of { selectedLocations, locationState, locations }
   * @returns {Promise<Array>}     Array of capture objects matching sendToWebhook's shape
   */
  async function captureAllLocationsLive(captureState) {
    const svc = window.MultiLocationScreenshotService;
    const captures = [];
    const originalActiveLocation = state.activeLocation;
    const isModalEditMode = window.CustomizerV2EditMode?.isModal;

    // In modal edit mode the user is staring at the canvas — hide the brief
    // location-switch flicker behind a translucent overlay.
    let overlay = null;
    if (isModalEditMode) {
      overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(255,255,255,0.6);z-index:99999;pointer-events:auto;';
      document.body.appendChild(overlay);
    }

    try {
      for (const locationId of captureState.selectedLocations) {
        const location = captureState.locations.find(l => l.id === locationId);
        if (!location) continue;

        // Switch the live canvas to this location
        state.activeLocation = locationId;
        renderProductImage();
        await waitForProductImage();

        // Clear the onload handler set by renderProductImage — it calls
        // renderBoundingBox() and can re-fire during the RAF yield in
        // captureLiveCanvas, re-adding the bounding box after we clear it.
        elements.productImage.onload = null;

        // Re-render overlays for this location's saved state
        clearFabricLogo();
        clearFabricText();
        renderBoundingBox(location);

        const locState = captureState.locationState[locationId] || {};
        if (locState.selectedLogo && !locState.noLogo) {
          await renderLogoOnCanvas(location, locState.selectedLogo.id, locState.selectedLogo.url);
        }
        if (locState.personalization?.text?.trim()) {
          renderTextOnCanvas();
        }

        // Hide editor chrome (bounding box + selection corners) before capturing
        // — otherwise the dashed placement rect and the yellow selection handles
        // get baked into the mockup. The next loop iteration re-renders both
        // when it switches to the next location, so no restore is needed here.
        clearBoundingBox();
        state.fabricCanvas.discardActiveObject();
        state.fabricCanvas.renderAll();

        // captureLiveCanvas waits for fonts.ready and one paint frame internally
        const fullMockup = await svc.captureLiveCanvas({
          productImage: elements.productImage,
          fabricCanvas: state.fabricCanvas
        });

        // Logo blob is grabbed from the live Fabric canvas (where renderLogoOnCanvas
        // has already drawn the logo with the customer's thread colors). Text uses
        // its own standalone capture path.
        let logo = null, text = null;
        if (locState.selectedLogo && !locState.noLogo) {
          logo = await svc.captureLogoFromFabric(state.fabricCanvas);
        }
        if (locState.personalization?.text?.trim()) {
          text = await svc.captureTextOnly(locState, getDefaultFont, loadFont);
        }

        captures.push({
          locationId,
          location,
          locState,
          blobs: { fullMockup, logo, text }
        });
      }
    } finally {
      // Restore original active location and re-render
      if (originalActiveLocation) {
        state.activeLocation = originalActiveLocation;
        const origLoc = captureState.locations.find(l => l.id === originalActiveLocation);
        if (origLoc) {
          renderProductImage();
          await waitForProductImage();
          clearFabricLogo();
          clearFabricText();
          renderBoundingBox(origLoc);
          const origState = captureState.locationState[originalActiveLocation] || {};
          if (origState.selectedLogo && !origState.noLogo) {
            await renderLogoOnCanvas(origLoc, origState.selectedLogo.id, origState.selectedLogo.url);
          }
          if (origState.personalization?.text?.trim()) {
            renderTextOnCanvas();
          }
        }
      }
      if (overlay) overlay.remove();
    }

    return captures;
  }

  /**
   * Capture all locations from the LIVE editor and upload via webhook.
   * Blobs are written to IndexedDB BEFORE the upload starts so the cart
   * drawer always has something to show, and canonical CDN URLs are only
   * persisted to _customizations after the webhook confirms success — that
   * way an order's URL never points at a file that was never uploaded.
   *
   * @param {Object} cartResponse        Shopify cart response (null in edit mode)
   * @param {string} customizationKey    Correlation key (e.g., "variant_123_0")
   * @param {Object} [stateSnapshot]     Optional state snapshot — pass one in
   *                                     edit mode where state may mutate before
   *                                     this background task runs.
   */
  /**
   * Build the deterministic locationUrls object used by both the cart-attribute
   * write AND the webhook upload. The filenames MUST be computed exactly once
   * per add-to-cart — the same timestamp has to end up in _customizations and
   * in the multipart upload, otherwise n8n writes one filename while the cart
   * references a different one.
   *
   * Returns { locationUrls, cartToken, timestamp }. cartToken is best-effort —
   * if /cart.js can't be fetched we still proceed with 'unknown' because the
   * deterministic path just folds into the filename and n8n treats it as opaque.
   *
   * @param {string[]} selectedLocations - snapshot of state.selectedLocations
   */
  async function buildDeterministicScreenshotUrls(selectedLocations) {
    const STORAGE_BASE_URL = 'https://assets.tee.digital';
    const storefrontCode = window.CustomizerV2Data?.storefront || 'default';
    const timestamp = Date.now();

    let cartToken = 'unknown';
    try {
      const cartData = await fetch('/cart.js').then(r => r.json());
      cartToken = cartData?.token?.split('?')[0] || 'unknown';
    } catch (e) {
      console.warn('[Screenshot] Failed to fetch cart token for URL pre-gen:', e);
    }

    const locationUrls = {};
    if (selectedLocations && selectedLocations.length > 0) {
      selectedLocations.forEach((locationId) => {
        const mockupFilename = `${storefrontCode}/${cartToken}_${locationId}_mockup_${timestamp}.png`;
        const logoFilename = `${storefrontCode}/${cartToken}_${locationId}_logo_${timestamp}.png`;
        const textFilename = `${storefrontCode}/${cartToken}_${locationId}_text_${timestamp}.png`;
        locationUrls[locationId] = {
          mockup: `${STORAGE_BASE_URL}/productMockups/${mockupFilename}`,
          logo: `${STORAGE_BASE_URL}/logoMockups/${logoFilename}`,
          text: `${STORAGE_BASE_URL}/textMockups/${textFilename}`,
          filenames: { mockup: mockupFilename, logo: logoFilename, text: textFilename }
        };
      });
    } else {
      const mockupFilename = `${storefrontCode}/${cartToken}_product_mockup_${timestamp}.png`;
      locationUrls['product'] = {
        mockup: `${STORAGE_BASE_URL}/productMockups/${mockupFilename}`,
        logo: null,
        text: null,
        filenames: { mockup: mockupFilename, logo: null, text: null }
      };
    }

    return { locationUrls, cartToken, timestamp };
  }

  /**
   * Capture and upload screenshots for a customization.
   *
   * IMPORTANT: URLs are NOT written to _customizations here. They are written
   * atomically in handleAddToCart/handleEditModeSave (via OrderAttributesBuilder)
   * before this function runs, using the exact same deterministic filenames we
   * upload with. That design removes a whole class of race conditions where a
   * second item's saveCustomizations would clobber the first item's URL write.
   *
   * If the webhook upload fails, the URLs are left in _customizations anyway —
   * the deterministic path means n8n can reconcile later (or a retry will land
   * at the same filename). Without that URL in cart, the order ships without
   * a mockup reference, which is what was happening before this fix.
   *
   * @param {Object} cartResponse        Shopify cart response (null in edit mode)
   * @param {string} customizationKey    Correlation key (e.g., "variant_123_0")
   * @param {Object} [stateSnapshot]     Optional state snapshot — pass one in
   *                                     edit mode where state may mutate before
   *                                     this background task runs.
   * @param {Object} [preComputed]       Pre-computed { locationUrls, cartToken,
   *                                     timestamp } from buildDeterministicScreenshotUrls.
   *                                     When provided, filenames match what we
   *                                     already wrote to _customizations. When
   *                                     absent (legacy callers), we generate
   *                                     new ones and the two won't match — this
   *                                     fallback exists only for non-customizer
   *                                     entry points like product-gifting-buttons.
   */
  async function captureAndUploadScreenshots(cartResponse, customizationKey, stateSnapshot = null, preComputed = null) {
    const svc = window.MultiLocationScreenshotService;
    if (!svc) {
      console.error('[Screenshot] MultiLocationScreenshotService not loaded');
      return;
    }

    // Snapshot synchronously, BEFORE entering the queue. If we waited until
    // the queued task ran we'd read state mutated by whatever the user did
    // next (clicked another color, switched location, opened a new variant).
    const captureState = stateSnapshot || {
      selectedLocations: [...state.selectedLocations],
      locationState: JSON.parse(JSON.stringify(state.locationState)),
      locations: state.locations
    };

    return svc.enqueue(async () => {
      try {
        const hasLocations = captureState.selectedLocations.length > 0;

        // Small delay so the cart drawer's open animation doesn't fight the capture loop
        await new Promise(resolve => setTimeout(resolve, 500));

        // Prefer pre-computed URLs from handleAddToCart. Fall back to generating
        // them inline for legacy callers (product-gifting-buttons, quick-add).
        let locationUrls, cartToken, timestamp;
        if (preComputed && preComputed.locationUrls) {
          locationUrls = preComputed.locationUrls;
          cartToken = preComputed.cartToken || 'unknown';
          timestamp = preComputed.timestamp || Date.now();
        } else {
          const generated = await buildDeterministicScreenshotUrls(captureState.selectedLocations);
          locationUrls = generated.locationUrls;
          cartToken = generated.cartToken;
          timestamp = generated.timestamp;
        }

        let captures;
        if (hasLocations) {
          captures = await captureAllLocationsLive(captureState);
        } else {
          const mockupBlob = await captureProductImageOnly();
          captures = [{
            locationId: 'product',
            location: { name: 'Product' },
            locState: {},
            blobs: { fullMockup: mockupBlob, logo: null, text: null }
          }];
        }

        // Persist blobs to IndexedDB before kicking off the upload — the cart
        // drawer reads from here, so it shows the preview instantly and stays
        // correct even if every webhook retry fails.
        for (const capture of captures) {
          const { locationId, blobs } = capture;
          if (blobs.fullMockup) {
            await svc.idbPut(svc.idbKey('mockup', customizationKey, locationId), blobs.fullMockup);
          }
          if (blobs.logo) {
            await svc.idbPut(svc.idbKey('logo', customizationKey, locationId), blobs.logo);
          }
          if (blobs.text) {
            await svc.idbPut(svc.idbKey('text', customizationKey, locationId), blobs.text);
          }
        }

        document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true }));

        const webhookOptions = {
          webhookUrl: window.SCREENSHOT_WEBHOOK_URL,
          metadata: {
            customizationKey: customizationKey,
            timestamp: timestamp,
            cartToken: cartToken,
            lineItemKey: cartResponse?.key || '',
            variantId: cartResponse?.variant_id || window.CustomizerV2Data.variantId,
            productId: cartResponse?.product_id || window.CustomizerV2Data.productId,
            storefront: window.CustomizerV2Data.storefront,
            shopDomain: window.CustomizerV2Data.shopDomain
          },
          locationUrls: locationUrls,
          maxRetries: 3
        };
        const webhookResult = await svc.sendToWebhook(captures, webhookOptions);

        // URLs are already in _customizations (written atomically at save time),
        // so a webhook failure no longer means the order ships without a mockup
        // reference. Log the failure for observability — n8n's deterministic
        // filenames let a retry or reconcile land at the exact path we stored.
        if (!webhookResult) {
          console.warn('[Screenshot] Upload failed for', customizationKey, '— URLs already persisted, n8n can reconcile via deterministic path');
        }
      } catch (error) {
        console.error('[Screenshot] captureAndUploadScreenshots failed for key:', customizationKey, error);
      }
    });
  }

  /**
   * Build line item properties with placeholder URLs
   * Actual URLs will be filled in by webhook later
   * @returns {Object} Properties object for cart line item
   */
  function buildPlaceholderLineItemProperties() {
    const data = window.CustomizerV2Data;
    const properties = {};

    // Global product metadata
    properties['_course_domain'] = data.shopDomain;
    properties['_storefront_code'] = data.storefront;
    if (data.productMeta?.productType) properties['_product_type'] = data.productMeta.productType;
    if (data.productMeta?.fulfiller) properties['_fulfiller'] = data.productMeta.fulfiller;
    if (data.productMeta?.supplier) properties['_supplier'] = data.productMeta.supplier;
    if (data.productMeta?.decorationType) properties['_decoration_type'] = data.productMeta.decorationType;
    if (data.productMeta?.brand) properties['_brand'] = data.productMeta.brand;

    // Variant title (if product has variants)
    const currentVariant = getCurrentVariant();
    if (currentVariant && currentVariant.title && currentVariant.title !== 'Default Title') {
      properties['_variant_title'] = currentVariant.title;
    }

    // Per-location properties (placeholder URLs)
    state.selectedLocations.forEach((locationId, i) => {
      const index = i + 1; // 1-based
      const location = state.locations.find(l => l.id === locationId);
      const locState = state.locationState[locationId];
      const selectedLogo = locState?.selectedLogo;
      const personalization = locState?.personalization;
      const logoPosition = locState?.logoPosition;
      const customColors = selectedLogo ? (locState.logoThreadColors[selectedLogo.id] || {}) : {};
      const svgThreadLayers = selectedLogo ? (locState.svgThreadLayers[selectedLogo.id] || []) : [];

      // Location identifier
      properties[`Location ${index}`] = location.name;

      // Product image for this location
      properties[`_product${index}_image`] = location.picture || data.fallbackImage;

      // Placeholder image URLs (will be replaced by webhook)
      const placeholderTimestamp = Date.now();
      properties[`Location ${index} Image`] = `https://assets.tee.digital/productMockups/placeholder_${placeholderTimestamp}_loc${index}.png`;
      properties[`_product_image_url`] = properties[`Location ${index} Image`];

      // Logo properties
      if (selectedLogo) {
        properties[`Logo ${index}`] = selectedLogo.id;
        properties[`Logo ${index} PNG`] = `https://assets.tee.digital/logoMockups/placeholder_${placeholderTimestamp}_logo${index}.png`;
        properties[`_logo_url`] = properties[`Logo ${index} PNG`];

        // Logo code
        properties[`_logo${index}Code`] = `${data.storefront}.${selectedLogo.id}`;

        // Logo coordinates (includes canvas dimensions for scaling when loaded in different contexts)
        if (logoPosition) {
          const canvasW = logoPosition.canvasWidth || state.fabricCanvas?.width || 600;
          const canvasH = logoPosition.canvasHeight || state.fabricCanvas?.height || 600;
          const coords = `${Math.round(logoPosition.left)}_${Math.round(logoPosition.top)}_${Math.round(logoPosition.scaleX * 100)}_${Math.round(logoPosition.scaleY * 100)}_${canvasW}_${canvasH}`;
          properties[`_logo${index}Coordinates`] = coords;
        } else if (location.defaultPlacement) {
          properties[`_logo${index}Coordinates`] = location.defaultPlacement;
        }

        // Logo rotation angle
        if (logoPosition?.angle) {
          properties[`_logo${index}RotateAngle`] = String(logoPosition.angle);
        }

        // Thread colors - build from layers
        const threadNames = [];
        const threadColors = [];
        const threadCodes = [];

        svgThreadLayers.forEach(layer => {
          const custom = customColors[layer.index];
          threadNames.push(custom?.name || layer.name);
          threadColors.push(custom?.hex || layer.defaultHex);
          threadCodes.push(custom?.code || layer.code || '');
        });

        if (threadNames.length > 0) {
          properties[`_logo${index}ThreadNames`] = threadNames.join(',');
          properties[`_logo${index}ThreadColors`] = threadColors.join(',');
          properties[`_logo${index}ThreadCodes`] = threadCodes.join(',');
        }
      }

      // Personalization properties
      if (personalization?.text?.trim()) {
        const text = personalization.text.trim();
        const threadColor = personalization.threadColor || getDefaultThreadColor();
        const position = personalization.position;

        // Text content
        properties[`Personalization ${index}`] = text;
        properties[`Personalization ${index} PNG`] = `https://assets.tee.digital/textMockups/placeholder_${placeholderTimestamp}_text${index}.png`;
        properties[`_text_url`] = properties[`Personalization ${index} PNG`];

        // Font info
        const selectedFont = personalization.font || getDefaultFont();
        properties[`Personalization ${index} Font`] = selectedFont.name;
        if (position?.fontSize) {
          properties[`Personalization ${index} Font Size`] = String(position.fontSize);
        }

        // Thread color
        properties[`Personalization ${index} Thread`] = threadColor.name;
        properties[`_personalization${index}ThreadColors`] = threadColor.hex;
        properties[`_personalization${index}ThreadCodes`] = threadColor.code || '';

        // Position coordinates (includes canvas dimensions for scaling)
        if (position) {
          const canvasW = position.canvasWidth || state.fabricCanvas?.width || 600;
          const canvasH = position.canvasHeight || state.fabricCanvas?.height || 600;
          const coords = `${Math.round(position.left)}_${Math.round(position.top)}_${Math.round(position.fontSize)}_${Math.round(position.angle || 0)}_${canvasW}_${canvasH}`;
          properties[`_personalization${index}Coordinates`] = coords;
        }
      }
    });

    return properties;
  }

  // ============================================
  // B2B ORDER ATTRIBUTES
  // Uses external module: order-attributes-builder.js
  // window.OrderAttributesBuilder.saveCustomizations(config)
  // ============================================

  /**
   * Build the metadata object for customization storage
   */
  function buildCustomizationMetadata() {
    const data = window.CustomizerV2Data;
    const currentVariant = getCurrentVariant();

    // Extract color (option1) and size (option2) from variant
    let color = '';
    let size = '';
    let variantTitle = '';

    if (currentVariant) {
      variantTitle = currentVariant.title || '';
      color = currentVariant.option1 || '';
      size = currentVariant.option2 || '';

      // If variant title is "Default Title", clear it
      if (variantTitle === 'Default Title') {
        variantTitle = '';
      }
    }

    return {
      storefront: data.storefront || '',
      shopDomain: data.shopDomain || '',
      productType: data.productMeta?.productType || '',
      fulfiller: data.productMeta?.fulfiller || '',
      supplier: data.productMeta?.supplier || '',
      decorationType: data.productMeta?.decorationType || '',
      brand: data.productMeta?.brand || '',
      fallbackImage: data.fallbackImage || '',
      variantTitle: variantTitle,
      color: color,
      size: size
    };
  }

  /**
   * Build the common config for customization save/update operations
   */
  function buildCustomizationConfig() {
    return {
      selectedLocations: state.selectedLocations,
      locations: state.locations,
      locationState: state.locationState,
      metadata: buildCustomizationMetadata(),
      canvasWidth: state.fabricCanvas?.width || 600,
      canvasHeight: state.fabricCanvas?.height || 600
    };
  }

  /**
   * Save customizations using the external OrderAttributesBuilder module.
   * Now saves for ALL sessions (not just B2B) - _customizations is the source of truth.
   * @param {Object} [locationUrls] - Pre-computed deterministic screenshot URLs
   *   (from buildDeterministicScreenshotUrls). When provided, urls are baked into
   *   the entry on the SAME cart update as the rest of the customization — no
   *   separate write to patch them in later.
   * @returns {Promise<{key: string, data: Object, minimalProperties: Object}|null>}
   */
  async function saveCustomizations(locationUrls = null) {
    if (!window.OrderAttributesBuilder) return null;
    return await window.OrderAttributesBuilder.saveCustomizations({
      variantId: state.selectedVariantId || window.CustomizerV2Data.variantId,
      ...buildCustomizationConfig(),
      locationUrls
    });
  }

  /**
   * Update existing customization for edit mode.
   * @param {string} customizationKey - The key to update
   * @param {Object} [locationUrls] - Pre-computed deterministic screenshot URLs.
   * @returns {Promise<{key: string, data: Object, minimalProperties: Object}|null>}
   */
  async function updateCustomization(customizationKey, locationUrls = null) {
    if (!window.OrderAttributesBuilder) return null;
    return await window.OrderAttributesBuilder.updateCustomization(
      customizationKey,
      { ...buildCustomizationConfig(), locationUrls }
    );
  }

  // ============================================
  // DOM ELEMENTS
  // ============================================
  const elements = {};

  // ============================================
  // INITIALIZATION
  // ============================================
  async function init() {
    if (!window.CustomizerV2Data) {
      return;
    }

    // Find customizer root NOT inside the modal (modal has duplicate IDs)
    const allRoots = document.querySelectorAll('#customizer-v2-root');
    let productPageRoot = null;
    for (const root of allRoots) {
      if (!root.closest('#customizer-edit-modal')) {
        productPageRoot = root;
        break;
      }
    }

    if (!productPageRoot) {
      // No product page customizer, skip (we're on cart/other page with only modal)
      return;
    }

    cacheElements();
    initializeVariantState();
    loadLocations();

    // Check for edit mode via URL param and sessionStorage
    const editData = checkForEditMode();
    if (editData) {
      // Parse cart properties and apply edit mode state
      setupEditMode(editData);
    } else {
      initializeLocationState();
    }

    initFabricCanvas();
    bindEvents();

    // If in edit mode, wait for preloaded content (includes render)
    if (window.CustomizerV2EditMode?.enabled) {
      // Wait for product image to load first
      await waitForProductImage();
      await renderPreloadedContent();
    } else {
      render();
    }
  }

  /**
   * Wait for the product. image to be fully loaded and have valid dimensions
   * @returns {Promise<void>}
   */
  function waitForProductImage() {
    return new Promise((resolve) => {
      const checkDimensions = () => {
        // Check if image is loaded AND has valid display dimensions
        if (elements.productImage.complete &&
            elements.productImage.clientWidth > 0 &&
            elements.productImage.clientHeight > 0) {
          resolve();
          return true;
        }
        return false;
      };

      // If already ready, resolve immediately
      if (checkDimensions()) return;

      // Set up load handler
      elements.productImage.onload = () => {
        // After load, wait for layout with requestAnimationFrame
        requestAnimationFrame(() => {
          if (!checkDimensions()) {
            // If still not ready, poll briefly
            let attempts = 0;
            const poll = () => {
              if (checkDimensions() || attempts++ > 20) {
                resolve();
              } else {
                requestAnimationFrame(poll);
              }
            };
            poll();
          }
        });
      };

      // Timeout fallbagck in case image fails to load
      setTimeout(resolve, 3000);
    });
  }

  /**
   * Check if we're in edit mode (editing existing cart item)
   * @returns {Object|null} Edit data or null
   */
  function checkForEditMode() {
    const urlParams = new URLSearchParams(window.location.search);
    const editCartItem = urlParams.get('edit_cart_item');

    if (!editCartItem) return null;

    // Get edit data from sessionStorage
    const storedData = sessionStorage.getItem('customizerEditData');

    if (!storedData) {
      return null;
    }

    try {
      const editData = JSON.parse(storedData);

      // Verify the line key matches
      if (editData.lineKey === editCartItem) {
        return editData;
      }
    } catch (e) {
      // Failed to parse edit data
    }

    return null;
  }

  /**
   * Set up edit mode with cart item data
   * @param {Object} editData - { lineKey, variantId, quantity, properties }
   */
  function setupEditMode(editData) {
    // Initialize location state first (with defaults)
    initializeLocationState();

    // Check if parser is available
    if (!window.CustomizerEditParser) {
      return;
    }

    // Parse cart properties into customizer state
    const parsedState = window.CustomizerEditParser.parseCartProperties(
      editData.properties,
      state.locations,
      window.CustomizerV2Data
    );

    if (parsedState && parsedState.selectedLocations.length > 0) {
      // Enhance with proper font objects
      if (window.CustomizerV2Data.personalizationFonts) {
        window.CustomizerEditParser.enhanceWithFonts(
          parsedState,
          window.CustomizerV2Data.personalizationFonts
        );
      }

      // Apply the parsed state
      applyEditModeState(parsedState);
    }

    // Extract _customization_key and minimal properties from cart properties
    // Properties can come in different formats from Shopify:
    // 1. Object: {"Location 1": "Left Chest", "_customization_key": "..."}
    // 2. Array of arrays: [["Location 1", "Left Chest"], ["_customization_key", "..."]]
    // 3. Array of objects with first/last: [{first: "Location 1", last: "Left Chest"}]
    let customizationKey = null;
    let minimalProperties = {};
    if (editData.properties) {
      const props = editData.properties;
      // Normalize properties to object format
      let propsObj = {};
      if (Array.isArray(props)) {
        props.forEach(p => {
          if (Array.isArray(p) && p.length >= 2) {
            propsObj[p[0]] = p[1];
          } else if (p && typeof p === 'object') {
            const key = p.first || p.name || p.key;
            const val = p.last || p.value;
            if (key !== undefined) {
              propsObj[key] = val;
            }
          }
        });
      } else if (typeof props === 'object') {
        propsObj = props;
      }

      customizationKey = propsObj['_customization_key'] || null;
      Object.keys(propsObj).forEach(key => {
        if (!key.startsWith('_')) minimalProperties[key] = propsObj[key];
      });
    }

    // Set edit mode flag
    window.CustomizerV2EditMode = {
      enabled: true,
      lineKey: editData.lineKey,
      quantity: editData.quantity,
      variantId: editData.variantId,
      customizationKey: customizationKey, // For updating _customizations
      minimalProperties: minimalProperties // For comparison on save
    };

    // Update button text
    if (elements.addToCart) {
      elements.addToCart.textContent = 'Save Changes';
    }

    // Clear sessionStorage
    sessionStorage.removeItem('customizerEditData');
  }

  /**
   * Cache DOM elements for the customizer
   * @param {HTMLElement} [container] - Optional container to scope queries (for modal context)
   */
  function cacheElements(container) {
    // Use container if provided (modal context), otherwise use document (product page context)
    const ctx = container || document;
    const byId = (id) => container ? container.querySelector(`#${id}`) : document.getElementById(id);

    elements.root = byId('customizer-v2-root');
    elements.productImage = byId('cv2-product-image');
    elements.canvas = byId('cv2-canvas');
    elements.mockupView = byId('cv2-mockup-view');
    elements.logoView = byId('cv2-logo-view');
    elements.logoDisplay = byId('cv2-logo-display');

    // Tabs
    elements.mainTabs = ctx.querySelectorAll('.cv2-main-tab');
    elements.customizeTabs = ctx.querySelectorAll('.cv2-customize-tab');

    // Variant option selectors (dynamic — queried by data-option-index)
    elements.optionSections = ctx.querySelectorAll('.cv2-option-section');

    // Location pills
    elements.locationPills = byId('cv2-location-pills');

    // Location tabs
    elements.locationTabs = byId('cv2-location-tabs');

    // Content areas
    elements.logoGallery = byId('cv2-logo-gallery');
    elements.threadLayers = byId('cv2-thread-layers');
    elements.customizeContent = byId('cv2-customize-content');
    elements.personalizeContent = byId('cv2-personalize-content');
    elements.customizeGroup = ctx.querySelector('.cv2-customize-group');
    elements.customizeTabsContainer = ctx.querySelector('.cv2-customize-tabs');
    elements.customizationCard = ctx.querySelector('.cv2-customization-card');

    // Quantity selector
    elements.quantityInput = byId('cv2-quantity');
    elements.quantityMinus = ctx.querySelector('.cv2-quantity-minus');
    elements.quantityPlus = ctx.querySelector('.cv2-quantity-plus');

    // Actions
    elements.addToCart = byId('cv2-add-to-cart');
  }

  function loadLocations() {
    const data = window.CustomizerV2Data;
    state.locations = data.locations || [];

    // Select first location by default if available
    if (state.locations.length > 0) {
      state.selectedLocations = [state.locations[0].id];
      state.activeLocation = state.locations[0].id;
    }

    // Set global flag for Logo Universal system to check
    window.customizerNoLocationsSelected = state.selectedLocations.length === 0;
  }

  function initializeLocationState() {
    // Initialize state for each location
    state.locations.forEach(location => {
      state.locationState[location.id] = {
        selectedLogo: null,
        logoPosition: null,
        logoThreadColors: {},  // Per-logo: { [logoId]: { [threadIndex]: { hex, name, code } } }
        svgThreadLayers: {},   // Per-logo: { [logoId]: [{ index, defaultHex, name, code }] }
        noLogo: false, // User opted to have no logo for this location
        personalization: {
          text: '',
          font: null, // Will use getDefaultFont() when null
          textAlign: 'center', // 'left' | 'center' | 'right'
          threadColor: null, // Resolved dynamically via getDefaultThreadColor()
          position: null, // { left, top, scaleX, scaleY, angle }
          enableTextResizing: false // Controls if text can be resized on canvas
        }
      };
    });
  }

  /**
   * Apply pre-populated state from parsed cart properties (edit mode)
   * @param {Object} parsedState - { selectedLocations, locationState }
   */
  function applyEditModeState(parsedState) {
    if (!parsedState) return;

    // Helper to normalize location ID for comparison
    const normalizeId = (id) => (id || '').toLowerCase().replace(/[\s_-]+/g, '-');

    // Helper to find matching location by ID or name
    const findMatchingLocation = (parsedId) => {
      const normalizedParsedId = normalizeId(parsedId);
      return state.locations.find(loc => {
        // Try exact match
        if (loc.id === parsedId) return true;
        // Try normalized ID match
        if (normalizeId(loc.id) === normalizedParsedId) return true;
        // Try name-based match
        if (normalizeId(loc.name) === normalizedParsedId) return true;
        return false;
      });
    };

    // Set selected locations from parsed state
    if (parsedState.selectedLocations && parsedState.selectedLocations.length > 0) {
      // Map parsed locations to available locations, handling ID mismatches
      const validLocations = [];
      const locationIdMap = {}; // Maps parsed ID -> actual ID

      parsedState.selectedLocations.forEach(parsedId => {
        const matchedLoc = findMatchingLocation(parsedId);
        if (matchedLoc) {
          validLocations.push(matchedLoc.id);
          locationIdMap[parsedId] = matchedLoc.id;
        }
      });

      if (validLocations.length > 0) {
        state.selectedLocations = validLocations;
        state.activeLocation = validLocations[0];

        // Remap locationState keys if IDs were different
        if (Object.keys(locationIdMap).length > 0) {
          const remappedLocationState = {};
          Object.entries(parsedState.locationState || {}).forEach(([parsedId, locState]) => {
            const actualId = locationIdMap[parsedId] || parsedId;
            remappedLocationState[actualId] = locState;
          });
          parsedState.locationState = remappedLocationState;
        }
      }
    }

    // Apply location state for each location
    state.locations.forEach(location => {
      const parsedLocState = parsedState.locationState?.[location.id];

      if (parsedLocState) {
        // Merge parsed state with defaults
        state.locationState[location.id] = {
          selectedLogo: parsedLocState.selectedLogo || null,
          logoPosition: parsedLocState.logoPosition || null,
          logoThreadColors: parsedLocState.logoThreadColors || {},
          svgThreadLayers: {}, // Will be reparsed when logo loads
          noLogo: parsedLocState.noLogo || false,
          personalization: {
            text: parsedLocState.personalization?.text || '',
            font: parsedLocState.personalization?.font || null,
            textAlign: parsedLocState.personalization?.textAlign || 'center',
            threadColor: parsedLocState.personalization?.threadColor || null,
            position: parsedLocState.personalization?.position || null,
            enableTextResizing: parsedLocState.personalization?.enableTextResizing ?? false
          }
        };
      } else {
        // Initialize with defaults if no parsed state
        state.locationState[location.id] = {
          selectedLogo: null,
          logoPosition: null,
          logoThreadColors: {},
          svgThreadLayers: {},
          noLogo: false,
          personalization: {
            text: '',
            font: null,
            textAlign: 'center',
            threadColor: null,
            position: null,
            enableTextResizing: false
          }
        };
      }
    });
  }

  /**
   * Save current product page context (state + elements) before modal takes over
   */
  function saveProductPageContext() {
    // Only save if we have a valid product page customizer
    if (!elements.root || !document.body.contains(elements.root)) {
      savedProductPageContext = null;
      return;
    }

    // Deep clone state (except fabricCanvas which can't be cloned)
    savedProductPageContext = {
      elements: { ...elements },
      fabricCanvas: state.fabricCanvas,
      customizarV2Data: window.CustomizerV2Data,
      state: {
        locations: [...state.locations],
        selectedLocations: [...state.selectedLocations],
        activeLocation: state.activeLocation,
        activeTab: state.activeTab,
        customizeTab: state.customizeTab,
        locationState: JSON.parse(JSON.stringify(state.locationState))
      }
    };
  }

  /**
   * Restore product page context after modal closes
   */
  function restoreProductPageContext() {
    if (!savedProductPageContext) {
      return;
    }

    // Restore elements
    Object.assign(elements, savedProductPageContext.elements);

    // Restore fabric canvas
    state.fabricCanvas = savedProductPageContext.fabricCanvas;

    // Restore CustomizerV2Data
    window.CustomizerV2Data = savedProductPageContext.customizarV2Data;

    // Restore state
    state.locations = savedProductPageContext.state.locations;
    state.selectedLocations = savedProductPageContext.state.selectedLocations;
    state.activeLocation = savedProductPageContext.state.activeLocation;
    state.activeTab = savedProductPageContext.state.activeTab;
    state.customizeTab = savedProductPageContext.state.customizeTab;
    state.locationState = savedProductPageContext.state.locationState;

    // Clear saved context
    savedProductPageContext = null;

    // Re-render product page customizer
    render();
  }

  /**
   * Initialize customizer in modal context (for editing existing cart items)
   * Called by CustomizerEditModal controller
   */
  async function initInModal() {
    if (!window.CustomizerV2Data) {
      return;
    }

    // Save product page context before taking over
    // This allows restoring when modal closes
    saveProductPageContext();

    // Get the modal container to scope element queries
    // This prevents conflicts when product page customizer is also in the DOM
    const modalContainer = document.getElementById('customizer-edit-modal');
    if (!modalContainer) {
      return;
    }

    cacheElements(modalContainer);
    loadLocations();

    // Check if we're in edit mode with pre-populated state
    if (window.CustomizerV2EditMode?.enabled && window.CustomizerV2EditMode.initialState) {
      // Enhance with proper font objects (match font names to available fonts)
      if (window.CustomizerV2Data.personalizationFonts && window.CustomizerEditParser) {
        window.CustomizerEditParser.enhanceWithFonts(
          window.CustomizerV2EditMode.initialState,
          window.CustomizerV2Data.personalizationFonts
        );
      }
      applyEditModeState(window.CustomizerV2EditMode.initialState);
    } else {
      initializeLocationState();
    }

    initFabricCanvas();
    bindEvents();

    // Update button text for edit mode
    if (window.CustomizerV2EditMode?.enabled && elements.addToCart) {
      elements.addToCart.textContent = 'Save Changes';
    }

    // If in edit mode, render the pre-loaded logos and text (await to ensure SVG parsing completes)
    if (window.CustomizerV2EditMode?.enabled) {
      await renderPreloadedContent();
    } else {
      render();
    }
  }

  /**
   * Render pre-loaded content when in edit mode
   * This loads logos and text from the restored state
   */
  async function renderPreloadedContent() {
    // For each selected location, load and render the logo if one was selected
    for (const locationId of state.selectedLocations) {
      const locState = state.locationState[locationId];
      const location = state.locations.find(l => l.id === locationId);

      if (locState?.selectedLogo && location) {
        // Parse the SVG to get thread layers (needed for proper color rendering)
        // The function stores results in state.locationState[location.id].svgThreadLayers[logoId]
        try {
          await parseLogoThreadLayers(location, locState.selectedLogo.id, locState.selectedLogo.url);
        } catch (e) {
          // Failed to parse SVG layers for preloaded logo
        }
      }

      // Load personalization font if present (needed for canvas rendering)
      if (locState?.personalization?.font) {
        await loadFont(locState.personalization.font);
      }
    }

    // Re-render with the loaded data
    render();

    // Render logo and text on canvas for active location
    if (state.activeLocation) {
      renderActiveLocationLogo();
      renderTextOnCanvas();
    }
  }

  function initFabricCanvas() {
    // Initialize Fabric.js canvas
    const canvas = elements.canvas;

    // Create Fabric canvas instance
    state.fabricCanvas = new fabric.Canvas(canvas.id, {
      selection: false, // Disable multi-select
      preserveObjectStacking: true
    });

    // Handle object movement - constrain within bounding box
    state.fabricCanvas.on('object:moving', handleLogoMoving);
    state.fabricCanvas.on('object:scaling', handleLogoScaling);
    state.fabricCanvas.on('object:scaled', handleLogoScaled);  // Final constraint after scaling completes
    state.fabricCanvas.on('object:modified', handleLogoModified);

    // Capture original position when interaction starts (before Fabric modifies it)
    state.fabricCanvas.on('mouse:down', handleMouseDown);
    state.fabricCanvas.on('before:transform', handleBeforeTransform);

    // Final constraint check when mouse is released
    state.fabricCanvas.on('mouse:up', handleMouseUp);

    // Two-way sync: when object is selected on canvas, switch to corresponding tab
    state.fabricCanvas.on('selection:created', handleCanvasSelectionCreated);
    state.fabricCanvas.on('selection:updated', handleCanvasSelectionCreated);
  }

  function handleCanvasSelectionCreated(e) {
    const obj = e.selected?.[0];
    if (!obj) return;

    // When logo is selected, switch to customize tab
    if (obj.isLogo && state.customizeTab !== 'customize') {
      state.customizeTab = 'customize';
      renderCustomizeTabs();
    }
    // When text is selected, switch to personalize tab
    else if (obj.isText && state.customizeTab !== 'personalize') {
      state.customizeTab = 'personalize';
      renderCustomizeTabs();
    }
  }

  function handleMouseDown(e) {
    // Capture original position when interaction starts
    // This is needed because Fabric modifies position during scaling with centeredScaling
    const obj = e.target;
    if (!obj || (!obj.isLogo && !obj.isText)) return;

    // Store original center position on the object
    obj._originalCenterX = obj.left;
    obj._originalCenterY = obj.top;
  }

  function handleBeforeTransform(e) {
    // Capture scale before transform starts - gives us reliable fallback
    const transform = e.transform;
    if (!transform || !transform.target) return;

    const obj = transform.target;
    if (!obj.isLogo && !obj.isText) return;

    // Store the scale before any modification
    obj._scaleBeforeTransform = {
      scaleX: obj.scaleX,
      scaleY: obj.scaleY
    };

    // Pre-calculate max allowed scale
    const location = getActiveLocation();
    if (!location) return;

    const bounds = getCurrentBounds(location, obj.isText);
    if (!bounds) return;

    const padding = obj.isText ? TEXT_MARGIN : 20;
    const maxWidth = bounds.width - padding;
    const maxHeight = bounds.height - padding;
    const maxScale = Math.min(maxWidth / obj.width, maxHeight / obj.height);

    obj._maxAllowedScale = maxScale;
  }

  function handleMouseUp(e) {
    // Final constraint check when mouse is released
    const obj = state.fabricCanvas.getActiveObject();
    if (!obj || (!obj.isLogo && !obj.isText)) return;

    // Clear stored position
    delete obj._originalCenterX;
    delete obj._originalCenterY;

    const location = getActiveLocation();
    if (!location) return;

    const bounds = getCurrentBounds(location, obj.isText);
    if (!bounds) return;

    // Force final position constraint
    constrainPosition(obj, bounds);
    state.fabricCanvas.renderAll();
  }

  function handleLogoMoving(e) {
    const obj = e.target;
    const location = getActiveLocation();
    if (!location || !obj) return;

    // Skip bounding box objects
    if (!obj.isLogo && !obj.isText) return;

    // Get bounding box (use personalization canvas for text)
    const bounds = getCurrentBounds(location, obj.isText);
    if (!bounds) return;

    // Constrain position within bounding box
    constrainPosition(obj, bounds);
  }

  function handleLogoScaling(e) {
    const obj = e.target;
    const location = getActiveLocation();
    if (!location || !obj) return;

    // Skip objects that aren't logo or text
    if (!obj.isLogo && !obj.isText) return;

    const bounds = getCurrentBounds(location, obj.isText);
    if (!bounds) {
      return;
    }

    const padding = obj.isText ? TEXT_MARGIN : 20;
    const maxWidth = bounds.width - padding;
    const maxHeight = bounds.height - padding;

    obj.setCoords();

    // TEXT OBJECTS: Convert scale to fontSize (matches old system)
    // During drag: use FAST font-metric estimate (no pixel scanning - too slow)
    // Accurate pixel scanning happens in handleLogoScaled() after drag ends
    if (obj.isText) {
      // Capture the scale user applied
      const scaleFactor = Math.min(obj.scaleX, obj.scaleY);

      // Convert to new fontSize
      const newFontSize = Math.round(obj.fontSize * scaleFactor);

      // FAST estimate: use font metrics (obj.width) - not pixel-perfect but responsive
      // Calculate rough max fontSize based on font metrics (fast during drag)
      const roughMaxFontSize = Math.floor((maxWidth / obj.width) * obj.fontSize * scaleFactor);

      // Constrain fontSize within bounds
      const constrainedFontSize = Math.min(newFontSize, roughMaxFontSize);
      const finalFontSize = Math.max(constrainedFontSize, MIN_FONT_SIZE);

      // Apply fontSize and reset scale to 1.0
      obj.set({
        fontSize: finalFontSize,
        scaleX: 1,
        scaleY: 1
      });

      // Reset transform to prevent Fabric from re-applying scale
      if (e.transform) {
        e.transform.scaleX = 1;
        e.transform.scaleY = 1;
      }

      obj.initDimensions();
      obj.setCoords();
      state.fabricCanvas.requestRenderAll();
      return;
    }

    // LOGO OBJECTS: Use original scale-based logic
    const boundingRect = obj.getBoundingRect(true, true);
    const currentWidth = boundingRect.width;
    const currentHeight = boundingRect.height;

    // Check if logo extends beyond bounding box edges
    const exceedsBounds = boundingRect.left < bounds.left ||
                          boundingRect.left + boundingRect.width > bounds.left + bounds.width ||
                          boundingRect.top < bounds.top ||
                          boundingRect.top + boundingRect.height > bounds.top + bounds.height;

    const exceedsSize = currentWidth > maxWidth || currentHeight > maxHeight;

    // Check if ACTUAL SIZE exceeds bounds OR object extends beyond bounding box
    if (exceedsSize || exceedsBounds) {
      // Calculate what scale would make it fit
      const scaleToFitWidth = maxWidth / obj.width;
      const scaleToFitHeight = maxHeight / obj.height;
      const maxScale = Math.min(scaleToFitWidth, scaleToFitHeight);

      // Apply the constrained scale
      obj.set({
        scaleX: maxScale,
        scaleY: maxScale
      });

      // Also modify the transform object
      if (e.transform) {
        e.transform.scaleX = maxScale;
        e.transform.scaleY = maxScale;
      }

      obj.setCoords();
      state.fabricCanvas.requestRenderAll();
    }
  }

  /**
   * Handle scaling completion - final constraint enforcement
   * This catches any scale that exceeded during the transform
   */
  function handleLogoScaled(e) {
    const obj = e.target;
    if (!obj || (!obj.isLogo && !obj.isText)) return;

    const location = getActiveLocation();
    if (!location) return;

    const bounds = getCurrentBounds(location, obj.isText);
    if (!bounds) return;

    // TEXT OBJECTS: Final fontSize enforcement using ACCURATE PIXEL SCANNING
    // This runs ONCE after scaling completes (not during drag)
    if (obj.isText) {
      const padding = TEXT_MARGIN;
      const maxWidth = bounds.width - padding;
      const maxHeight = bounds.height - padding;

      // If scale somehow isn't 1.0, convert to fontSize first
      if (obj.scaleX !== 1 || obj.scaleY !== 1) {
        const scaleFactor = Math.min(obj.scaleX, obj.scaleY);
        const newFontSize = Math.round(obj.fontSize * scaleFactor);
        obj.set({
          fontSize: newFontSize,
          scaleX: 1,
          scaleY: 1
        });
        obj.initDimensions();
        obj.setCoords();
      }

      // NOW do ACCURATE pixel scanning to ensure text actually fits
      // This uses Fabric.js rendering, so it's 100% accurate
      let currentFontSize = obj.fontSize;
      const fontFamily = obj.fontFamily || 'Arial, sans-serif';
      let iterations = 0;
      const MAX_ITERATIONS = 50;

      while (iterations < MAX_ITERATIONS && currentFontSize > MIN_FONT_SIZE) {
        const pixelBounds = measureTextPixelBounds(obj.text, currentFontSize, fontFamily);

        if (pixelBounds.width <= maxWidth && pixelBounds.height <= maxHeight) {
          break;  // Fits!
        }

        // Reduce fontSize
        const reduction = currentFontSize > 50 ? 5 : (currentFontSize > 20 ? 2 : 1);
        currentFontSize -= reduction;
        iterations++;
      }

      currentFontSize = Math.max(currentFontSize, MIN_FONT_SIZE);

      // Apply the verified fontSize
      if (currentFontSize !== obj.fontSize) {
        obj.set({ fontSize: currentFontSize });
        obj.initDimensions();
        obj.setCoords();
      }

      state.fabricCanvas.renderAll();
      return;
    }

    // LOGO OBJECTS: Use original scale-based logic
    const padding = 20;
    const maxWidth = bounds.width - padding;
    const maxHeight = bounds.height - padding;

    obj.setCoords();

    const boundingRect = obj.getBoundingRect(true, true);
    const currentWidth = boundingRect.width;
    const currentHeight = boundingRect.height;

    const exceedsBounds = boundingRect.left < bounds.left ||
                          boundingRect.left + boundingRect.width > bounds.left + bounds.width ||
                          boundingRect.top < bounds.top ||
                          boundingRect.top + boundingRect.height > bounds.top + bounds.height;

    const exceedsSize = currentWidth > maxWidth || currentHeight > maxHeight;

    // Final enforcement - if actual size exceeds bounds, snap it back
    if (exceedsSize || exceedsBounds) {
      const maxScale = Math.min(maxWidth / obj.width, maxHeight / obj.height);

      obj.set({
        scaleX: maxScale,
        scaleY: maxScale
      });
      obj.setCoords();
      state.fabricCanvas.renderAll();
    }
  }

  function constrainPosition(obj, bounds) {
    // Force update of bounding rect before checking
    obj.setCoords();

    // Use Fabric's getBoundingRect for accurate bounds
    // For text objects, this now includes the padding we set
    const objBounds = obj.getBoundingRect(true, true);

    // Add boundary padding (keeps object away from the canvas edge)
    // Text objects already have Fabric padding for glyph overflow, so we use less here
    const boundaryPadding = obj.isText ? 10 : 0;

    let newLeft = obj.left;
    let newTop = obj.top;
    let constrained = false;

    // For center-origin objects, obj.left/top is the center point
    // The offset calculation still works the same way
    const leftOffset = obj.left - objBounds.left;
    const topOffset = obj.top - objBounds.top;

    // Left boundary
    if (objBounds.left < bounds.left + boundaryPadding) {
      newLeft = bounds.left + boundaryPadding + leftOffset;
      constrained = true;
    }
    // Right boundary
    if (objBounds.left + objBounds.width > bounds.left + bounds.width - boundaryPadding) {
      newLeft = bounds.left + bounds.width - objBounds.width - boundaryPadding + leftOffset;
      constrained = true;
    }
    // Top boundary
    if (objBounds.top < bounds.top + boundaryPadding) {
      newTop = bounds.top + boundaryPadding + topOffset;
      constrained = true;
    }
    // Bottom boundary
    if (objBounds.top + objBounds.height > bounds.top + bounds.height - boundaryPadding) {
      newTop = bounds.top + bounds.height - objBounds.height - boundaryPadding + topOffset;
      constrained = true;
    }

    if (constrained) {
      obj.set({ left: newLeft, top: newTop });
      obj.setCoords();
    }
  }

  function handleLogoModified(e) {
    const obj = e.target;
    const location = getActiveLocation();
    if (!location || !obj) return;

    // Handle text object modification
    if (obj.isText) {
      handleTextModified(e);
      return;
    }

    // Handle logo object modification
    if (!obj.isLogo) return;

    // Save position to state (including rotation and canvas dimensions for screenshot scaling)
    state.locationState[location.id].logoPosition = {
      left: obj.left,
      top: obj.top,
      scaleX: obj.scaleX,
      scaleY: obj.scaleY,
      angle: obj.angle,
      canvasWidth: state.fabricCanvas.width,
      canvasHeight: state.fabricCanvas.height
    };
  }

  function getCurrentBounds(location, forText = false) {
    // Get current scaled bounding box coordinates
    const img = elements.productImage;
    // Use personalization canvas for text, fallback to logo canvas, then defaultPlacement
    const canvasCoords = forText
      ? (parseCoords(location.personalizationCanvas) || parseCoords(location.canvas) || parseCoords(location.defaultPlacement))
      : (parseCoords(location.canvas) || parseCoords(location.defaultPlacement));
    if (!canvasCoords) return null;

    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;
    const originalWidth = img.naturalWidth || 2048;
    const originalHeight = img.naturalHeight || 2048;
    const scaleX = displayWidth / originalWidth;
    const scaleY = displayHeight / originalHeight;

    return {
      left: canvasCoords.x * scaleX,
      top: canvasCoords.y * scaleY,
      width: canvasCoords.width * scaleX,
      height: canvasCoords.height * scaleY
    };
  }

  // ============================================
  // EVENT BINDING
  // ============================================
  function bindEvents() {
    // Option selectors (event delegation for dynamically rendered swatches/pills)
    elements.optionSections.forEach(section => {
      const selector = section.querySelector('.cv2-option-selector');
      if (selector) {
        selector.addEventListener('click', handleOptionSwatchClick);
      }
    });

    // Main tabs (Mockup/Logo)
    elements.mainTabs.forEach(tab => {
      tab.addEventListener('click', handleMainTabClick);
    });

    // Customize/Personalize tabs
    elements.customizeTabs.forEach(tab => {
      tab.addEventListener('click', handleCustomizeTabClick);
    });

    // Close thread picker when clicking outside
    document.addEventListener('click', handleThreadPickerOutsideClick);

    // Quantity selector
    if (elements.quantityMinus) {
      elements.quantityMinus.addEventListener('click', handleQuantityMinus);
    }
    if (elements.quantityPlus) {
      elements.quantityPlus.addEventListener('click', handleQuantityPlus);
    }
    if (elements.quantityInput) {
      elements.quantityInput.addEventListener('change', handleQuantityChange);
    }

    // Add to cart
    elements.addToCart.addEventListener('click', handleAddToCart);
  }

  // ============================================
  // EVENT HANDLERS
  // ============================================

  function handleOptionSwatchClick(e) {
    // Handle both color swatches and text pills
    const swatch = e.target.closest('.cv2-variant-swatch, .cv2-size-swatch');
    if (!swatch || swatch.disabled) return;

    const selectorEl = swatch.closest('.cv2-option-selector');
    const optionIndex = parseInt(selectorEl?.dataset.optionIndex, 10);
    if (isNaN(optionIndex)) return;

    const selectedValue = swatch.dataset.optionValue;
    if (!selectedValue || state.selectedOptions[optionIndex] === selectedValue) return;

    // Update selected options
    state.selectedOptions[optionIndex] = selectedValue;

    // Find matching variant from all selected options
    const data = window.CustomizerV2Data;
    const optionKeys = ['option1', 'option2', 'option3'];
    let candidates = data.variants;

    // Filter by all options up to and including the clicked one
    for (let i = 0; i <= optionIndex; i++) {
      const val = state.selectedOptions[i];
      if (val) {
        const key = optionKeys[i];
        candidates = candidates.filter(v => v[key] === val);
      }
    }

    // Try to preserve downstream selections, otherwise pick first available
    let targetVariant = null;
    const optionCount = data.optionNames?.length || 1;
    if (optionIndex < optionCount - 1) {
      // Try to keep downstream option values
      let narrowed = candidates;
      for (let i = optionIndex + 1; i < optionCount; i++) {
        const val = state.selectedOptions[i];
        if (val) {
          const key = optionKeys[i];
          const further = narrowed.filter(v => v[key] === val);
          if (further.length > 0) {
            narrowed = further;
          } else {
            // Can't preserve this downstream option, clear it
            state.selectedOptions[i] = null;
          }
        }
      }
      targetVariant = narrowed.find(v => v.available) || narrowed[0] || candidates.find(v => v.available) || candidates[0];
    } else {
      targetVariant = candidates.find(v => v.available) || candidates[0];
    }

    if (!targetVariant) return;

    // Sync selectedOptions from the resolved variant
    for (let i = 0; i < optionCount; i++) {
      state.selectedOptions[i] = targetVariant[optionKeys[i]];
    }

    // Handle the variant change (async - fetches new locations)
    handleVariantChange(targetVariant.id);
  }

  function handleMainTabClick(e) {
    const tab = e.currentTarget.dataset.tab;
    if (tab === state.activeTab) return;

    state.activeTab = tab;
    renderMainTabs();
    renderImageView();

    // Re-render the logo on mockup when switching back to mockup tab
    if (tab === 'mockup') {
      renderActiveLocationLogo();
    }
  }

  function handleCustomizeTabClick(e) {
    const tab = e.currentTarget.dataset.customize;
    if (tab === state.customizeTab) return;

    state.customizeTab = tab;
    renderCustomizeTabs();

    // Two-way sync: when switching tabs, select corresponding object on canvas
    syncCanvasSelectionWithTab();
  }

  function handleQuantityMinus() {
    if (!elements.quantityInput) return;
    const current = parseInt(elements.quantityInput.value, 10) || 1;
    const min = parseInt(elements.quantityInput.min, 10) || 1;
    if (current > min) {
      elements.quantityInput.value = current - 1;
    }
  }

  function handleQuantityPlus() {
    if (!elements.quantityInput) return;
    const current = parseInt(elements.quantityInput.value, 10) || 1;
    const max = parseInt(elements.quantityInput.max, 10) || 999;
    if (current < max) {
      elements.quantityInput.value = current + 1;
    }
  }

  function handleQuantityChange() {
    if (!elements.quantityInput) return;
    let value = parseInt(elements.quantityInput.value, 10);
    const min = parseInt(elements.quantityInput.min, 10) || 1;
    const max = parseInt(elements.quantityInput.max, 10) || 999;

    if (isNaN(value) || value < min) {
      value = min;
    } else if (value > max) {
      value = max;
    }
    elements.quantityInput.value = value;
  }

  function syncCanvasSelectionWithTab() {
    if (!state.fabricCanvas) return;

    const objects = state.fabricCanvas.getObjects();
    const logoObj = objects.find(obj => obj.isLogo);
    const textObj = objects.find(obj => obj.isText);

    if (state.customizeTab === 'customize') {
      // Switching to customize tab - select logo if exists, else deselect
      if (logoObj) {
        state.fabricCanvas.setActiveObject(logoObj);
      } else {
        state.fabricCanvas.discardActiveObject();
      }
    } else if (state.customizeTab === 'personalize') {
      // Switching to personalize tab - select text if exists, else deselect
      if (textObj) {
        state.fabricCanvas.setActiveObject(textObj);
      } else {
        state.fabricCanvas.discardActiveObject();
      }
    }

    state.fabricCanvas.requestRenderAll();
  }

  function handleLocationPillClick(e) {
    const pill = e.currentTarget;
    const locationId = pill.dataset.locationId;
    const isCurrentlySelected = state.selectedLocations.includes(locationId);
    const isAddingNew = !isCurrentlySelected;

    if (isCurrentlySelected) {
      // Deselect - remove from selected locations
      state.selectedLocations = state.selectedLocations.filter(id => id !== locationId);
      // If we removed the active location, select the first remaining one
      if (state.activeLocation === locationId) {
        state.activeLocation = state.selectedLocations[0] || null;
      }
      // Reset location state so re-adding starts fresh with defaults
      state.locationState[locationId] = {
        selectedLogo: null,
        logoPosition: null,
        logoThreadColors: {},
        svgThreadLayers: {},
        noLogo: false,
        personalization: {
          text: '',
          font: null,
          textAlign: 'center',
          threadColor: null,
          position: null,
          enableTextResizing: true
        }
      };
    } else {
      // Select - add to selected locations
      state.selectedLocations.push(locationId);
      // Set as active location
      state.activeLocation = locationId;
    }

    // Update global flag for Logo Universal system
    window.customizerNoLocationsSelected = state.selectedLocations.length === 0;

    // IMPORTANT: Capture whether this location already has a logo BEFORE renderLogoGallery runs
    // renderLogoGallery may auto-select the first logo, which would make this check always true after
    const hadPreviousLogo = isAddingNew && state.locationState[locationId]?.selectedLogo;

    renderLocationPills();
    renderLocationTabs();
    renderProductImage();
    renderLogoGallery();
    renderCustomizeTabs();
    // Skip renderActiveLocationLogo when adding NEW location with no prior selection
    // renderLogoGallery handles initial auto-select via initializeFirstLogo
    // But if re-enabling a location that already has a logo selected, we need to render it
    if (!isAddingNew) {
      renderActiveLocationLogo();
    } else if (hadPreviousLogo) {
      // Re-enabling location with existing selection from previous state (edit mode)
      // Note: If !hadPreviousLogo, renderLogoGallery already handled it via initializeFirstLogo
      renderActiveLocationLogo();
    }
    renderTextOnCanvas();
    renderThreadLayers();
    renderPersonalizeContent();
    updateAddToCartButton();

    // Update Logo tab view if it's active
    if (state.activeTab === 'logo') {
      renderLogoInLogoView();
    }
  }

  function handleLocationTabClick(e) {
    const locationId = e.currentTarget.dataset.locationId;
    if (locationId === state.activeLocation) return;

    state.activeLocation = locationId;

    // Update tab classes without re-rendering
    elements.locationTabs.querySelectorAll('.cv2-location-tab').forEach(tab => {
      const isActive = tab.dataset.locationId === locationId;
      tab.classList.toggle('cv2-location-tab--active', isActive);
    });

    // Animate indicator to new position
    positionLocationTabsIndicator();

    renderProductImage();
    renderLogoGallery();
    renderActiveLocationLogo();
    renderTextOnCanvas(); // Restore text for this location
    renderThreadLayers();
    renderMainTabs(); // Update mockup/logo tab visibility based on noLogo state
    renderImageView(); // Sync viewer display with tab state (e.g. switch from logo to mockup)
    renderCustomizeTabs(); // Update customize/personalize tabs based on noLogo state
    renderPersonalizeContent(); // Update personalization UI for this location

    // Update Logo tab view if it's active
    if (state.activeTab === 'logo') {
      renderLogoInLogoView();
    }
  }

  /**
   * Check if any selected location has noLogo but no personalization text.
   * Disables/enables the add-to-cart button accordingly.
   */
  function updateAddToCartButton() {
    if (!elements.addToCart) return;
    const needsText = state.selectedLocations.some(locId => {
      const locState = state.locationState[locId];
      return locState?.noLogo && !locState?.personalization?.text?.trim();
    });
    elements.addToCart.disabled = needsText;

    // Collapse panel height when no locations selected
    const panel = elements.addToCart.closest('.cv2-panel');
    if (panel) {
      panel.classList.toggle('cv2-panel--compact', state.selectedLocations.length === 0);
    }
  }

  /**
   * Handle Add to Cart button click - SINGLE SOURCE OF TRUTH FLOW
   * 1. Save to _customizations first (source of truth)
   * 2. Get minimal properties for checkout display
   * 3. Add to Shopify cart with minimal properties
   * 4. Show success to user
   * 5. Background: Capture and upload screenshots via webhook (using customization key)
   */
  async function handleAddToCart() {
    const button = elements.addToCart;
    const originalText = button.textContent;
    const isEditMode = window.CustomizerV2EditMode?.enabled;

    try {
      // Update button state
      button.disabled = true;
      button.textContent = isEditMode ? 'Saving...' : 'Adding to cart...';

      if (isEditMode) {
        // Edit mode: Update _customizations, optionally update cart item
        await handleEditModeSave();
        return;
      }

      // Step 0: Pre-compute deterministic screenshot URLs. We MUST do this
      // before saveCustomizations so the urls can be written into the entry
      // in the same cart update. Doing it after would re-introduce the race
      // where a second item's save clobbers the first item's urls.
      const preComputed = await buildDeterministicScreenshotUrls([...state.selectedLocations]);

      // Step 1: Save to _customizations WITH urls baked in (single atomic write)
      const customizationResult = await saveCustomizations(preComputed.locationUrls);

      // Step 2: Get minimal properties for checkout display
      let properties = {};
      if (customizationResult) {
        properties = customizationResult.minimalProperties;
      } else {
        // Fallback to full properties if OrderAttributesBuilder not available
        properties = buildPlaceholderLineItemProperties();
      }

      // Always add product-based line item properties (immutable after order placement)
      const data = window.CustomizerV2Data;
      if (data.productMeta?.productType) properties['_product_type'] = data.productMeta.productType;
      if (data.productMeta?.fulfiller) properties['_fulfiller'] = data.productMeta.fulfiller;
      if (data.productMeta?.supplier) properties['_supplier'] = data.productMeta.supplier;
      if (data.productMeta?.decorationType) properties['_decoration_type'] = data.productMeta.decorationType;

      // Blank image: variant image → first product media image
      const variant = data.variants?.find(v => v.id === (state.selectedVariantId || data.variantId));
      const blankImage = variant?.featured_image || data.productFirstImage || data.productImage;
      if (blankImage) properties['_blank_image'] = blankImage;

      // Step 3: Add to Shopify cart with minimal properties
      const cartResponse = await addToShopifyCart(properties);

      // Show success immediately
      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);

      // Step 4: Background - Capture and upload screenshots via webhook.
      // Pass the same preComputed URLs so the uploaded filenames match the
      // ones we already wrote into _customizations.
      if (customizationResult?.key) {
        captureAndUploadScreenshots(cartResponse, customizationResult.key, null, preComputed);
      }

    } catch (error) {
      console.error('Add to cart error:', error);
      alert('There was an error adding the item to the cart. Please try again.');
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  /**
   * Handle save in edit mode - SIMPLIFIED FLOW
   * Now just updates _customizations (source of truth)
   * Only removes/re-adds cart item if minimal properties changed
   */
  async function handleEditModeSave() {
    const button = elements.addToCart;
    const editMode = window.CustomizerV2EditMode;

    try {
      // Pre-compute deterministic screenshot URLs so the cart-attribute write
      // and the capture upload reference the exact same filenames. Without this,
      // a concurrent save for another item could clobber the urls between the
      // save and the post-capture patch.
      const preComputed = await buildDeterministicScreenshotUrls([...state.selectedLocations]);

      // Step 1: Update _customizations using the existing customization key
      let customizationResult;
      if (editMode.customizationKey) {
        // Update existing customization
        customizationResult = await updateCustomization(editMode.customizationKey, preComputed.locationUrls);
      } else {
        // No key means legacy item - save as new customization
        customizationResult = await saveCustomizations(preComputed.locationUrls);
      }

      // Safety check: if customizationResult failed or has no locations, don't clear the cart item
      if (!customizationResult || !customizationResult.minimalProperties) {
        alert('Failed to save changes. The customization data could not be updated.');
        button.disabled = false;
        button.textContent = 'Save Changes';
        return;
      }

      // Verify _customization_key exists (required for linking to _customizations data)
      if (!customizationResult.minimalProperties['_customization_key']) {
        alert('Failed to save changes. Please close and try again.');
        button.disabled = false;
        button.textContent = 'Save Changes';
        return;
      }

      // Step 2: No need to update line item properties
      // Line item only has _customization_key which never changes
      // All customization display data comes from _customizations (updated in Step 1)
      // This keeps line item key stable - no /cart/change.js call needed

      // Step 3: Capture new screenshots in background
      // IMPORTANT: Snapshot state NOW before starting background process
      // This prevents race conditions when user edits multiple items quickly
      const stateSnapshot = {
        selectedLocations: [...state.selectedLocations],
        locationState: JSON.parse(JSON.stringify(state.locationState)),
        locations: state.locations
      };

      // Pass the customization key, state snapshot, and the same pre-computed
      // URLs we wrote into _customizations so the upload uses matching filenames.
      captureAndUploadScreenshots(null, customizationResult?.key, stateSnapshot, preComputed).catch(() => {
        // Screenshot capture failed, but customization + urls already saved
      });

      // Show success
      button.textContent = 'Saved!';

      // Check if we're in modal context
      if (editMode.isModal && window.CustomizerEditModal) {
        setTimeout(() => {
          // Reset button state before closing (so it's ready for next open)
          button.disabled = false;
          button.textContent = 'Save Changes';
          window.CustomizerEditModal.close();

          // Refresh cart display (line item ID unchanged, no full reload needed)
          window.CustomizerEditModal.refreshCart();
          if (window.CustomizationDisplayManager) {
            window.CustomizationDisplayManager.render();
          }
        }, 500);
      } else {
        // Redirect back to cart after brief delay
        setTimeout(() => {
          window.location.href = '/cart';
        }, 500);
      }

    } catch (error) {
      alert('Failed to save changes. Please try again.');
      button.disabled = false;
      button.textContent = 'Save Changes';
    }
  }

  // ============================================
  // OLD FUNCTIONS REMOVED - Now using reusable screenshot service
  // ============================================
  // The following functions have been removed:
  // - generateAndUploadLocationImages()
  // - generateFullMockupPng()
  // - drawSvgOnCanvas()
  // - generateLogoPng()
  // - generateTextPng()
  // - uploadImagesToService()
  // - buildLineItemProperties()
  //
  // Replaced with external service (multi-location-screenshot-service.js):
  // - window.MultiLocationScreenshotService.captureAllLocations() (off-screen rendering)
  // - window.MultiLocationScreenshotService.sendToWebhook() (webhook upload)
  // - buildPlaceholderLineItemProperties() (instant properties)
  // - captureAndUploadScreenshots() (service wrapper)
  // ============================================

  /**
   * Add item to Shopify cart
   */
  async function addToShopifyCart(properties) {
    const data = window.CustomizerV2Data;

    // Get quantity from input, default to 1
    const quantity = elements.quantityInput
      ? parseInt(elements.quantityInput.value, 10) || 1
      : 1;

    const variantId = state.selectedVariantId || data.variantId;

    // Check if variant is available
    const variant = data.variants?.find(v => v.id === variantId);
    if (variant && !variant.available) {
      throw new Error('Selected variant is not available');
    }

    // Use FormData format (same as theme's cart add)
    // This ensures compatibility with all stores and apps
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Add properties in the format Shopify expects for FormData
    if (properties && typeof properties === 'object') {
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      const responseText = await response.text();
      let errorText = responseText;
      try {
        const errorJson = JSON.parse(responseText);
        errorText = errorJson.description || errorJson.message || responseText;
      } catch {
        // Not JSON, use raw text
      }
      throw new Error(`Cart add failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();

    // Trigger cart update events for different themes
    // Motion theme listens to ajaxProduct:added
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: {
        product: result,
        addToCartBtn: elements.addToCart
      },
      bubbles: true
    }));

    // Pipeline theme uses theme:cart:popdown with variant detail
    // This triggers the cart popdown or drawer depending on settings
    document.dispatchEvent(new CustomEvent('theme:cart:popdown', {
      detail: {
        variant: result
      },
      bubbles: true
    }));

    // Pipeline cart drawer: force re-init to fetch fresh cart data
    // theme:cart:reload alone doesn't refetch, it uses cached cart object
    document.dispatchEvent(new CustomEvent('theme:cart:init', {
      bubbles: true
    }));

    // Then trigger reload to update the display
    document.dispatchEvent(new CustomEvent('theme:cart:reload', {
      bubbles: true
    }));

    return result;
  }

  async function handleLogoClick(e) {
    const button = e.currentTarget;
    const logoId = button.dataset.logoId;
    const logoUrl = button.dataset.logoUrl;
    const location = getActiveLocation();

    if (!location) return;

    // Prevent logo selection if user has opted out of logo for this location
    if (state.locationState[location.id]?.noLogo) return;

    // Update state - keep thread colors saved per-logo
    state.locationState[location.id].selectedLogo = {
      id: logoId,
      url: logoUrl
    };

    // Update selection visually (don't rebuild gallery)
    elements.logoGallery.querySelectorAll('.cv2-logo-item').forEach(item => {
      item.classList.toggle('cv2-logo-item--selected', item.dataset.logoId === logoId);
    });

    // Parse thread layers from SVG (only if not already parsed for this logo)
    const locState = state.locationState[location.id];
    if (!locState.svgThreadLayers[logoId]) {
      await parseLogoThreadLayers(location, logoId, logoUrl);
    }

    // Render logo on canvas (mockup view)
    renderLogoOnCanvas(location, logoId, logoUrl);

    // Update thread layers panel
    renderThreadLayers();

    // Update Logo tab view if it's active
    if (state.activeTab === 'logo') {
      renderLogoInLogoView();
    }
  }

  /**
   * Handle no-logo checkbox toggle
   * When checked: clear logo from canvas, deselect logo, hide logo tab
   * When unchecked: restore logo selection capability
   */
  function handleNoLogoToggle(e) {
    const location = getActiveLocation();
    if (!location) return;

    const noLogo = e.target.checked;

    // Update location state
    state.locationState[location.id].noLogo = noLogo;

    if (noLogo) {
      // Clear selected logo
      state.locationState[location.id].selectedLogo = null;

      // Clear logo from canvas (keep bounding box for personalization)
      clearFabricLogo();

      // Force mockup view if on logo tab
      if (state.activeTab === 'logo') {
        state.activeTab = 'mockup';
        renderMockupView();
      }

      // Hide logo gallery and label with animation
      const galleryItems = elements.logoGallery.querySelector('.cv2-logo-gallery-items');
      if (galleryItems) galleryItems.classList.add('cv2-logo-gallery-items--hidden');
      const logoLabel = elements.logoGallery.parentElement?.querySelector('.cv2-label');
      if (logoLabel) logoLabel.classList.add('cv2-label--hidden');
      elements.logoGallery.parentElement?.classList.add('cv2-section--no-logo');

      // Add disabled state to gallery items
      elements.logoGallery.querySelectorAll('.cv2-logo-item').forEach(item => {
        item.classList.add('cv2-logo-item--disabled');
        item.classList.remove('cv2-logo-item--selected');
      });
    } else {
      // Show logo gallery and label with animation
      const galleryItems = elements.logoGallery.querySelector('.cv2-logo-gallery-items');
      if (galleryItems) galleryItems.classList.remove('cv2-logo-gallery-items--hidden');
      const logoLabel = elements.logoGallery.parentElement?.querySelector('.cv2-label');
      if (logoLabel) logoLabel.classList.remove('cv2-label--hidden');
      elements.logoGallery.parentElement?.classList.remove('cv2-section--no-logo');

      // Remove disabled state from gallery items
      elements.logoGallery.querySelectorAll('.cv2-logo-item').forEach(item => {
        item.classList.remove('cv2-logo-item--disabled');
      });

      // Auto-select first logo (same as when adding a location)
      let logos = resolveLocationLogos(location);
      // Filter out ONLYPERSONALIZATION marker
      logos = logos.filter(id => id.toUpperCase() !== 'ONLYPERSONALIZATION');

      if (logos.length > 0) {
        const firstLogoId = logos[0];
        const firstLogoUrl = `${window.CustomizerV2Data.logoStorageUrl}/${window.CustomizerV2Data.storefront}.${firstLogoId}.svg`;

        state.locationState[location.id].selectedLogo = {
          id: firstLogoId,
          url: firstLogoUrl
        };

        // Update the gallery to show the selection visually
        const firstItem = elements.logoGallery.querySelector(`[data-logo-id="${firstLogoId}"]`);
        if (firstItem) {
          firstItem.classList.add('cv2-logo-item--selected');
        }

        // Parse thread layers and render logo
        initializeFirstLogo(location, firstLogoId, firstLogoUrl);
      }
    }

    // Update main tabs (hide/show logo tab)
    renderMainTabs();

    // Update customize tabs (hide Customize tab if noLogo)
    renderCustomizeTabs();

    // Update thread layers (clear if no logo)
    renderThreadLayers();

    // Update add-to-cart button state
    updateAddToCartButton();
  }

  /**
   * Parse the SVG to extract thread layers (g tags with placeholder fills)
   * Stores per-logo in state.locationState[locationId].svgThreadLayers[logoId]
   */
  async function parseLogoThreadLayers(location, logoId, logoUrl) {
    try {
      const response = await fetch(logoUrl);
      if (!response.ok) return;

      const svgText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');

      // Find all <g> tags with fill attributes
      const gTags = doc.querySelectorAll('g[fill]');
      const threadLayers = [];
      let layerCounter = 0; // Unique sequential index for every layer

      gTags.forEach((gTag, gTagIdx) => {
        const fillValue = gTag.getAttribute('fill');
        const gTagId = gTag.getAttribute('id'); // Get the layer id (e.g., "layer_1")
        // Switchable thread placeholder — exact match against a fixed set of
        // 5 marker hexes (see SWITCHABLE_PLACEHOLDER_SLOTS above). Anything
        // else, including real library colors like #5e3424 (Coffee Bean
        // Brown), falls through to the fixed-hex branch below.
        const switchableSlotIdx = getSwitchablePlaceholderSlot(fillValue);

        if (switchableSlotIdx !== undefined) {
          // Each <g> tag gets its own layer — no deduplication
          const originalThreadIndex = switchableSlotIdx;

          // Get default color from threadInstructions
          const threadInstructions = location.threadInstructions || [];
          const defaultThread = threadInstructions[originalThreadIndex];

          let hex, name, code;
          if (defaultThread) {
            if (Array.isArray(defaultThread)) {
              [hex, name, code] = defaultThread;
            } else {
              hex = defaultThread.hex;
              name = defaultThread.name;
              code = defaultThread.code;
            }
          }

          // fillValue is always a marker hex here (#1E4287, #2ED8C4, ...),
          // never a real color, so we must NOT render it. Fall back to neutral
          // grey only if threadInstructions doesn't define this slot.
          const fallbackHex = '#808080';

          // Always try to look up from thread library to get name/code
          const lookupHex = hex || fallbackHex;
          if (window.threadLookup) {
            const normalizedHex = lookupHex.toLowerCase().replace('#', '');
            // Try both with and without # prefix (thread library keys may vary)
            const threadFromLibrary = window.threadLookup[normalizedHex] ||
                                      window.threadLookup['#' + normalizedHex];
            if (threadFromLibrary) {
              // Use thread library values if not already set
              if (!name) name = threadFromLibrary.name;
              if (!code) code = threadFromLibrary.code;
              // Also use the library hex if we only had the fallback
              if (!hex && threadFromLibrary.hex) {
                hex = threadFromLibrary.hex.startsWith('#') ? threadFromLibrary.hex : `#${threadFromLibrary.hex}`;
              }
            }
          }

          threadLayers.push({
            index: layerCounter++,
            gTagForEachIndex: gTagIdx, // Position in querySelectorAll for matching in applyThreadColors
            gTagId: gTagId,
            originalThreadIndex: originalThreadIndex,
            placeholder: fillValue,
            defaultHex: hex || fallbackHex,
            name: name || 'Color',
            code: code || '',
            isFixedHex: false,
            isEditable: true
          });
        } else if (fillValue && /^#[0-9a-f]{3,6}$/i.test(fillValue)) {
          // Fixed hex color - each <g> tag is independent (no deduplication)
          const normalizedHex = fillValue.toLowerCase();

          // Try to find matching thread in library
          const hexWithoutHash = normalizedHex.replace('#', '');
          const matchedThread = window.threadLookup?.[hexWithoutHash] ||
                                window.threadLookup?.[normalizedHex];

          if (matchedThread) {
            // Found in thread library - make it editable
            threadLayers.push({
              index: layerCounter++,
              gTagForEachIndex: gTagIdx,
              gTagId: gTagId,
              placeholder: fillValue,
              defaultHex: matchedThread.hex?.startsWith('#') ? matchedThread.hex : `#${matchedThread.hex}`,
              name: matchedThread.name,
              code: matchedThread.code || '',
              isFixedHex: true,
              isEditable: true,
              originalHex: fillValue
            });
          } else {
            // Not found in thread library - still editable
            threadLayers.push({
              index: layerCounter++,
              gTagForEachIndex: gTagIdx,
              gTagId: gTagId,
              placeholder: fillValue,
              defaultHex: fillValue,
              name: `Color`,
              code: '',
              isFixedHex: true,
              isEditable: true,
              originalHex: fillValue
            });
          }
        }
      });

      // Layers are in SVG order (no sorting needed)

      // Store in state per-logo
      state.locationState[location.id].svgThreadLayers[logoId] = threadLayers;

    } catch (err) {
      // Silent fail - thread layers parsing failed
    }
  }

  // ============================================
  // VARIANT MANAGEMENT
  // ============================================

  /**
   * Initialize variant state from CustomizerV2Data
   */
  function initializeVariantState() {
    const data = window.CustomizerV2Data;
    state.selectedVariantId = data.variantId || null;

    // Initialize selectedOptions from the current variant
    const currentVariant = data.variants?.find(v => v.id === state.selectedVariantId);
    const optionCount = data.optionNames?.length || 0;
    state.selectedOptions = [];
    if (currentVariant) {
      const optionKeys = ['option1', 'option2', 'option3'];
      for (let i = 0; i < optionCount; i++) {
        state.selectedOptions[i] = currentVariant[optionKeys[i]] || null;
      }
    }

    // Cache initial locations for the current variant
    if (state.selectedVariantId && data.locations) {
      state.variantLocationsCache[state.selectedVariantId] = data.locations;
    }
  }

  /**
   * Get the currently selected variant object
   */
  function getCurrentVariant() {
    const data = window.CustomizerV2Data;
    if (!data.variants || !state.selectedVariantId) return null;
    return data.variants.find(v => v.id === state.selectedVariantId) || null;
  }

  /**
   * Check if an option name represents a color option (should use swatch rendering)
   */
  function isColorOption(optionName) {
    const colorKeywords = ['color', 'colour'];
    return colorKeywords.includes((optionName || '').toLowerCase());
  }

  /**
   * Get the option key for a given index (option1, option2, option3)
   */
  function getOptionKey(index) {
    return ['option1', 'option2', 'option3'][index] || 'option1';
  }

  /**
   * Get candidate variants filtered by all selected options up to (but not including) the given index
   */
  function getFilteredVariants(upToIndex) {
    const data = window.CustomizerV2Data;
    let candidates = data.variants;
    for (let i = 0; i < upToIndex; i++) {
      const val = state.selectedOptions[i];
      if (val) {
        const key = getOptionKey(i);
        candidates = candidates.filter(v => v[key] === val);
      }
    }
    return candidates;
  }

  /**
   * Render all variant option selectors dynamically
   */
  function renderVariantSelector() {
    const data = window.CustomizerV2Data;
    if (!data.hasVariants || !data.variants || data.variants.length <= 1) return;

    const optionNames = data.optionNames || [];
    if (optionNames.length === 0) return;

    optionNames.forEach((optionName, index) => {
      const section = document.querySelector(`.cv2-option-section[data-option-index="${index}"]`);
      if (!section) return;

      const selectorEl = section.querySelector('.cv2-option-selector');
      const nameEl = section.querySelector('.cv2-option-name');
      if (!selectorEl) return;

      if (isColorOption(optionName)) {
        renderColorSwatchesForOption(selectorEl, nameEl, index, section);
      } else {
        renderTextPillsForOption(selectorEl, nameEl, index, section);
      }
    });
  }

  /**
   * Render color swatches for an option (circles with background color/image)
   */
  function renderColorSwatchesForOption(selectorEl, nameEl, optionIndex, section) {
    const data = window.CustomizerV2Data;
    const optionKey = getOptionKey(optionIndex);
    const candidates = getFilteredVariants(optionIndex);

    // Group by this option's value
    const groups = new Map();
    candidates.forEach(variant => {
      const value = variant[optionKey] || variant.title;
      if (!groups.has(value)) {
        groups.set(value, {
          value: value,
          variants: [],
          swatch_color: variant.swatch_color,
          swatch_image: variant.swatch_image
        });
      }
      groups.get(value).variants.push(variant);
    });

    // If only one option value, hide the section
    if (groups.size <= 1) {
      section.classList.add('cv2-hidden');
      return;
    } else {
      section.classList.remove('cv2-hidden');
    }

    const useImageSwatches = data.useImageSwatches;
    const currentValue = state.selectedOptions[optionIndex];

    const swatchesHtml = Array.from(groups.values()).map(group => {
      const isSelected = group.value === currentValue;
      const isUnavailable = group.variants.every(v => !v.available);

      const swatchStyle = useImageSwatches && group.swatch_image
        ? `background-image: url('${group.swatch_image}')`
        : group.swatch_color
          ? `background-color: ${group.swatch_color}`
          : 'background-color: #f0f0f0';

      const swatchTypeClass = useImageSwatches ? 'cv2-variant-swatch--image' : 'cv2-variant-swatch--color';

      return `
        <button
          type="button"
          class="cv2-variant-swatch ${swatchTypeClass} ${isSelected ? 'cv2-variant-swatch--selected' : ''} ${isUnavailable ? 'cv2-variant-swatch--unavailable' : ''}"
          data-option-value="${group.value}"
          style="${swatchStyle}"
          title="${group.value}${isUnavailable ? ' (Sold out)' : ''}"
          ${isUnavailable ? 'disabled' : ''}
        ></button>
      `;
    }).join('');

    selectorEl.innerHTML = swatchesHtml;

    if (nameEl) {
      nameEl.textContent = currentValue || '';
    }
  }

  /**
   * Render text pills for an option (buttons with text labels)
   */
  function renderTextPillsForOption(selectorEl, nameEl, optionIndex, section) {
    const optionKey = getOptionKey(optionIndex);
    const candidates = getFilteredVariants(optionIndex);

    // Build unique values for this option (preserving order)
    const options = [];
    const seen = new Set();
    candidates.forEach(v => {
      const value = v[optionKey];
      if (value && !seen.has(value)) {
        seen.add(value);
        // Check if ANY variant with this value (within filtered candidates) is available
        const isAvailable = candidates.some(c => c[optionKey] === value && c.available);
        options.push({ value, available: isAvailable });
      }
    });

    // If only one option value, hide the section
    if (options.length <= 1) {
      section.classList.add('cv2-hidden');
      return;
    } else {
      section.classList.remove('cv2-hidden');
    }

    const currentValue = state.selectedOptions[optionIndex];

    const swatchesHtml = options.map(opt => {
      const isSelected = opt.value === currentValue;
      const selectedClass = isSelected ? 'cv2-size-swatch--selected' : '';
      const unavailableClass = !opt.available ? 'cv2-size-swatch--unavailable' : '';
      return `
        <button
          type="button"
          class="cv2-size-swatch ${selectedClass} ${unavailableClass}"
          data-option-value="${opt.value}"
          title="${opt.value}${!opt.available ? ' (Sold out)' : ''}"
          ${!opt.available ? 'disabled' : ''}
        >${opt.value}</button>`;
    }).join('');

    selectorEl.innerHTML = swatchesHtml;

    if (nameEl) {
      nameEl.textContent = currentValue || '';
    }
  }

  /**
   * Fetch location data for a specific variant via AJAX
   */
  async function fetchVariantLocations(variantId) {
    // Return cached data if available
    if (state.variantLocationsCache[variantId]) {
      return state.variantLocationsCache[variantId];
    }

    const data = window.CustomizerV2Data;
    try {
      const response = await fetch(
        `/products/${data.productHandle}?section_id=customizer-data-api&variant=${variantId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch variant data: ${response.status}`);
      }

      const html = await response.text();

      // Parse JSON from script tag in response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const jsonScript = doc.querySelector('#CustomizerV2DataJSON');

      if (!jsonScript) {
        console.error('No CustomizerV2DataJSON found in response');
        return [];
      }

      const locationData = JSON.parse(jsonScript.textContent);

      // Cache the result
      state.variantLocationsCache[variantId] = locationData.locations || [];
      return state.variantLocationsCache[variantId];
    } catch (err) {
      console.error('Error fetching variant locations:', err);
      return [];
    }
  }

  /**
   * Handle variant change - fetch new locations and reset state
   */
  async function handleVariantChange(variantId) {
    if (variantId === state.selectedVariantId) return;

    const data = window.CustomizerV2Data;

    // Update state
    state.selectedVariantId = variantId;

    // Update the global CustomizerV2Data for cart submission
    window.CustomizerV2Data.variantId = variantId;

    // Sync selectedOptions from the new variant
    const currentVariant = getCurrentVariant();
    const optionCount = window.CustomizerV2Data.optionNames?.length || 0;
    for (let i = 0; i < optionCount; i++) {
      state.selectedOptions[i] = currentVariant?.[getOptionKey(i)] || null;
    }

    // Update inventory display if it exists
    const availabilityEl = document.querySelector('.cv2-product-availability em');
    if (availabilityEl && currentVariant) {
      availabilityEl.textContent = `Available: ${currentVariant.inventory_quantity || 0}`;
    }

    // Fetch new locations for this variant
    const newLocations = await fetchVariantLocations(variantId);

    // Update locations
    state.locations = newLocations;

    // Reset all customization state
    state.selectedLocations = [];
    state.activeLocation = null;
    state.locationState = {};

    // Select first location by default if available
    if (state.locations.length > 0) {
      state.selectedLocations = [state.locations[0].id];
      state.activeLocation = state.locations[0].id;
    }

    // Re-initialize location state for new locations
    initializeLocationState();

    // Clear fabric canvas
    if (state.fabricCanvas) {
      state.fabricCanvas.clear();
    }

    // Re-render everything
    render();
  }

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  function render() {
    renderVariantSelector();
    renderLocationPills();
    renderLocationTabs();
    renderMainTabs();
    renderCustomizeTabs();
    renderProductImage();
    renderLogoGallery();
    // Note: Don't call renderActiveLocationLogo() here - initializeFirstLogo() handles initial render
    // renderActiveLocationLogo() is called when switching locations
    renderImageView();
    renderThreadLayers();
    renderPersonalizeContent();
    updateAddToCartButton();
  }

  function renderLocationPills() {
    // Render pills for each available location
    const pillsHtml = state.locations.map(location => {
      const isSelected = state.selectedLocations.includes(location.id);
      return `
        <button
          class="cv2-location-pill ${isSelected ? 'cv2-location-pill--selected' : ''}"
          data-location-id="${location.id}"
        >
          <span class="cv2-location-pill-text">${location.name}</span>
          ${isSelected ? `
            <svg class="cv2-location-pill-x" viewBox="0 0 24 24" width="14" height="14">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
            </svg>
          ` : ''}
        </button>
      `;
    }).join('');

    elements.locationPills.innerHTML = pillsHtml;

    // Bind pill click events
    elements.locationPills.querySelectorAll('.cv2-location-pill').forEach(pill => {
      pill.addEventListener('click', handleLocationPillClick);
    });
  }

  function renderLocationTabs() {
    // Only show tabs if more than one location is selected
    if (state.selectedLocations.length <= 1) {
      elements.locationTabs.innerHTML = '';
      elements.locationTabs.style.display = 'none';
      return;
    }

    elements.locationTabs.style.display = 'flex';

    // Build tabs HTML
    const tabsHtml = state.selectedLocations.map(locationId => {
      const location = state.locations.find(l => l.id === locationId);
      if (!location) return '';

      const isActive = locationId === state.activeLocation;

      return `
        <button
          class="cv2-location-tab ${isActive ? 'cv2-location-tab--active' : ''}"
          data-location-id="${locationId}"
        >
          ${location.name}
        </button>
      `;
    }).join('');

    // Add indicator element before tabs
    elements.locationTabs.innerHTML = '<div class="cv2-location-tabs-indicator"></div>' + tabsHtml;

    // Bind tab click events
    elements.locationTabs.querySelectorAll('.cv2-location-tab').forEach(tab => {
      tab.addEventListener('click', handleLocationTabClick);
    });

    // Position indicator on active tab (no animation on initial render)
    positionLocationTabsIndicator(false);
  }

  function positionLocationTabsIndicator(animate = true) {
    const indicator = elements.locationTabs.querySelector('.cv2-location-tabs-indicator');
    const activeTab = elements.locationTabs.querySelector('.cv2-location-tab--active');

    if (!indicator || !activeTab) return;

    const containerRect = elements.locationTabs.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const offsetLeft = tabRect.left - containerRect.left;

    // Disable transition for instant positioning when not animating
    if (!animate) {
      indicator.style.transition = 'none';
    }

    indicator.style.width = `${tabRect.width}px`;
    indicator.style.transform = `translateX(${offsetLeft - 4}px)`; // -4 for container padding

    // Re-enable transition after position is set
    if (!animate) {
      // Force reflow to apply the instant position before re-enabling transition
      indicator.offsetHeight;
      indicator.style.transition = '';
    }
  }

  function renderMainTabs() {
    const tabsContainer = document.querySelector('.cv2-main-tabs');
    const indicator = document.querySelector('.cv2-main-tabs-indicator');

    // Check if user has selected "no logo" for current location
    const location = getActiveLocation();
    const locationState = location ? state.locationState[location.id] : null;
    const userNoLogo = locationState?.noLogo || false;

    // Hide Logo tab if Hide Logo setting is enabled OR user selected "no logo"
    const logoTab = document.querySelector('[data-tab="logo"]');
    if (shouldHideLogo() || userNoLogo) {
      if (logoTab) logoTab.style.display = 'none';
      // Force mockup view if currently on logo tab
      if (state.activeTab === 'logo') {
        state.activeTab = 'mockup';
      }
    } else {
      if (logoTab) logoTab.style.display = '';
    }

    elements.mainTabs.forEach(tab => {
      const isActive = tab.dataset.tab === state.activeTab;
      tab.classList.toggle('cv2-main-tab--active', isActive);

      // Position indicator to match active tab
      if (isActive && indicator && tabsContainer) {
        const containerRect = tabsContainer.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const offsetLeft = tabRect.left - containerRect.left;

        indicator.style.width = `${tabRect.width}px`;
        indicator.style.transform = `translateX(${offsetLeft - 4}px)`; // -4 to account for container padding
      }
    });
  }

  function renderCustomizeTabs() {
    // Check feature settings
    const showRecolor = hasLogoSetting('Recolor');
    const showPersonalize = hasPersonalizationSetting('Show');

    // Check if user has selected "no logo" for current location
    const location = getActiveLocation();
    const locationState = location ? state.locationState[location.id] : null;
    const userNoLogo = locationState?.noLogo || false;

    // Hide customize tab if noLogo is checked (no logo to customize)
    const effectiveShowRecolor = showRecolor && !userNoLogo;

    // Hide entire group if no location is selected
    if (state.selectedLocations.length === 0) {
      if (elements.customizeGroup) {
        elements.customizeGroup.style.display = 'none';
      }
      return;
    }

    // If neither customize (recolor) nor personalize is available, hide the entire group
    if (!effectiveShowRecolor && !showPersonalize) {
      if (elements.customizeGroup) {
        elements.customizeGroup.style.display = 'none';
      }
      return;
    }

    // Show the group if at least one tab has content
    if (elements.customizeGroup) {
      elements.customizeGroup.style.display = '';
    }

    // Hide customize tab if Recolor not enabled or noLogo is checked
    const customizeTab = document.querySelector('[data-customize="customize"]');
    if (customizeTab) {
      customizeTab.style.display = effectiveShowRecolor ? '' : 'none';
    }

    // Hide personalize tab if Show not enabled
    const personalizeTab = document.querySelector('[data-customize="personalize"]');
    if (personalizeTab) {
      personalizeTab.style.display = showPersonalize ? '' : 'none';
    }

    // If only one tab is visible, hide the tabs container entirely (no need for tabs)
    if (elements.customizeTabsContainer) {
      const bothVisible = effectiveShowRecolor && showPersonalize;
      elements.customizeTabsContainer.style.display = bothVisible ? '' : 'none';
    }

    // Auto-switch to available tab if current tab is hidden
    if (!effectiveShowRecolor && state.customizeTab === 'customize') {
      state.customizeTab = 'personalize';
    }
    if (!showPersonalize && state.customizeTab === 'personalize') {
      state.customizeTab = 'customize';
    }

    // Update data-active attribute on container for sliding indicator
    if (elements.customizeTabsContainer) {
      elements.customizeTabsContainer.dataset.active = state.customizeTab;
    }

    elements.customizeTabs.forEach(tab => {
      const isActive = tab.dataset.customize === state.customizeTab;
      tab.classList.toggle('cv2-customize-tab--active', isActive);
    });

    // Toggle content visibility
    elements.customizeContent.style.display = (state.customizeTab === 'customize' && effectiveShowRecolor) ? 'block' : 'none';
    elements.personalizeContent.style.display = (state.customizeTab === 'personalize' && showPersonalize) ? 'block' : 'none';
  }

  function renderImageView() {
    // Toggle between mockup and logo views
    elements.mockupView.style.display = state.activeTab === 'mockup' ? 'block' : 'none';
    elements.logoView.style.display = state.activeTab === 'logo' ? 'flex' : 'none';

    // If showing logo view, render the selected logo
    if (state.activeTab === 'logo') {
      renderLogoInLogoView();
    }
  }

  async function renderLogoInLogoView() {
    const location = getActiveLocation();
    const locState = location ? state.locationState[location.id] : null;
    const selectedLogo = locState?.selectedLogo;

    if (!selectedLogo) {
      elements.logoDisplay.innerHTML = '<p class="cv2-placeholder">Select a logo to view</p>';
      return;
    }

    // Get custom colors for THIS specific logo
    const customColors = locState?.logoThreadColors?.[selectedLogo.id] || {};

    // Get thread layers for fixed hex color support
    const threadLayers = locState?.svgThreadLayers?.[selectedLogo.id] || null;

    // Hide immediately to prevent flicker
    elements.logoDisplay.style.opacity = '0';

    try {
      const response = await fetch(selectedLogo.url);
      if (!response.ok) throw new Error(`Failed to fetch logo: ${response.status}`);

      let svgText = await response.text();

      // Apply thread colors (including custom colors and fixed hex support)
      svgText = applyThreadColors(svgText, location.threadInstructions, location.switchy, customColors, threadLayers);

      // Display the full logo
      elements.logoDisplay.innerHTML = svgText;

      // Style the SVG for full view
      const svg = elements.logoDisplay.querySelector('svg');
      if (svg) {
        svg.style.maxWidth = '100%';
        svg.style.maxHeight = '100%';
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }

      // Fade in
      elements.logoDisplay.style.opacity = '1';
    } catch (err) {
      elements.logoDisplay.innerHTML = '<p class="cv2-placeholder">Error loading logo</p>';
      elements.logoDisplay.style.opacity = '1';
    }
  }

  function renderProductImage() {
    const location = getActiveLocation();
    const hasSelectedLocations = state.selectedLocations.length > 0;

    // Use fallback image if no location OR no locations are selected for customization
    if (!location || !hasSelectedLocations) {
      // Fallback chain: variant featured image → product first image → fallback
      const variant = getCurrentVariant();
      const fallbackImage = variant?.featured_image
        || window.CustomizerV2Data.productFirstImage
        || window.CustomizerV2Data.fallbackImage
        || '';
      elements.productImage.src = fallbackImage;
      clearCanvas();
      return;
    }

    // Use location picture, with fallback chain
    const variant = getCurrentVariant();
    const imageUrl = location.picture
      || variant?.featured_image
      || window.CustomizerV2Data.productFirstImage
      || window.CustomizerV2Data.fallbackImage
      || '';
    elements.productImage.src = imageUrl;

    // Render bounding box after image loads
    elements.productImage.onload = () => {
      renderBoundingBox(location);
    };

    // If image already cached, render immediately
    if (elements.productImage.complete) {
      renderBoundingBox(location);
    }
  }

  function renderLogoGallery() {
    const section = elements.logoGallery.closest('.cv2-section');
    const location = getActiveLocation();

    // Hide entire customization card if no location is selected
    if (!location || state.selectedLocations.length === 0) {
      elements.logoGallery.innerHTML = '';
      if (elements.customizationCard) elements.customizationCard.style.display = 'none';
      return;
    }

    // Show customization card (in case it was previously hidden)
    if (elements.customizationCard) elements.customizationCard.style.display = '';

    // Hide entire logo section if Hide Logo setting is enabled
    if (shouldHideLogo()) {
      elements.logoGallery.innerHTML = '';
      if (section) section.style.display = 'none';
      return;
    }

    // Show section (in case it was previously hidden)
    if (section) section.style.display = '';

    // Get logos from location (Liquid-resolved or JS fallback via storefrontLogoLists)
    let logos = resolveLocationLogos(location);

    // Check if personalization is enabled (ONLYPERSONALIZATION marker present)
    const hasPersonalization = logos.some(id => id.toUpperCase() === 'ONLYPERSONALIZATION');

    // Filter out ONLYPERSONALIZATION - it's a marker, not a real logo
    logos = logos.filter(id => id.toUpperCase() !== 'ONLYPERSONALIZATION');

    if (logos.length === 0 && !hasPersonalization) {
      elements.logoGallery.innerHTML = '<p class="cv2-placeholder">No logos available for this location</p>';
      return;
    }

    if (logos.length === 0 && hasPersonalization) {
      elements.logoGallery.innerHTML = '<p class="cv2-placeholder">Personalization only (no logos)</p>';
      return;
    }

    // Get currently selected logo for this location
    const locationState = state.locationState[location.id];
    const selectedLogoId = locationState?.selectedLogo?.id || null;

    // Render logo thumbnails
    const galleryHtml = logos.map(logoId => {
      const logoUrl = `${window.CustomizerV2Data.logoStorageUrl}/${window.CustomizerV2Data.storefront}.${logoId}.svg`;
      const isSelected = logoId === selectedLogoId;
      return `
        <button class="cv2-logo-item ${isSelected ? 'cv2-logo-item--selected' : ''}" data-logo-id="${logoId}" data-logo-url="${logoUrl}">
          <div class="cv2-logo-item-inner" data-logo-container="${logoId}">
            <div class="cv2-logo-item-spinner"></div>
          </div>
        </button>
      `;
    }).join('');

    // Check if noLogo is enabled for this location
    const noLogoEnabled = locationState?.noLogo || false;

    // Build checkbox HTML if User Hide Logo setting is enabled
    const noLogoCheckboxHtml = hasUserHideLogo() ? `
      <label class="cv2-no-logo-checkbox">
        <input type="checkbox" id="cv2-no-logo-checkbox" ${noLogoEnabled ? 'checked' : ''}>
        <span class="cv2-resize-checkbox-box">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="cv2-resize-checkbox-label">Only personalization for this location</span>
      </label>
    ` : '';

    // Wrap gallery in a container and add checkbox below
    elements.logoGallery.innerHTML = `
      <div class="cv2-logo-gallery-items${noLogoEnabled ? ' cv2-logo-gallery-items--hidden' : ''}">${galleryHtml}</div>
      ${noLogoCheckboxHtml}
    `;

    // Apply disabled/hidden state if noLogo is enabled
    if (noLogoEnabled) {
      elements.logoGallery.querySelectorAll('.cv2-logo-item').forEach(item => {
        item.classList.add('cv2-logo-item--disabled');
      });
      const logoLabel = elements.logoGallery.parentElement?.querySelector('.cv2-label');
      if (logoLabel) logoLabel.classList.add('cv2-label--hidden');
      elements.logoGallery.parentElement?.classList.add('cv2-section--no-logo');
    } else {
      const logoLabel = elements.logoGallery.parentElement?.querySelector('.cv2-label');
      if (logoLabel) logoLabel.classList.remove('cv2-label--hidden');
      elements.logoGallery.parentElement?.classList.remove('cv2-section--no-logo');
    }

    // Bind click events to logo items
    elements.logoGallery.querySelectorAll('.cv2-logo-item').forEach(item => {
      item.addEventListener('click', handleLogoClick);
    });

    // Bind no-logo checkbox event
    const noLogoCheckbox = document.getElementById('cv2-no-logo-checkbox');
    if (noLogoCheckbox) {
      noLogoCheckbox.addEventListener('change', handleNoLogoToggle);
    }

    // Load SVG thumbnails
    loadLogoThumbnails(logos);

    // Auto-select first logo if none selected and noLogo is not enabled
    // Only auto-select if this location is actually selected for customization
    const isLocationSelected = state.selectedLocations.includes(location.id);
    if (!selectedLogoId && logos.length > 0 && isLocationSelected && !noLogoEnabled) {
      const firstLogoId = logos[0];
      const firstLogoUrl = `${window.CustomizerV2Data.logoStorageUrl}/${window.CustomizerV2Data.storefront}.${firstLogoId}.svg`;

      state.locationState[location.id].selectedLogo = {
        id: firstLogoId,
        url: firstLogoUrl
      };

      // Update the gallery to show the selection visually
      const firstItem = elements.logoGallery.querySelector(`[data-logo-id="${firstLogoId}"]`);
      if (firstItem) {
        firstItem.classList.add('cv2-logo-item--selected');
      }

      // Parse thread layers and render logo (async)
      initializeFirstLogo(location, firstLogoId, firstLogoUrl);
    }
  }

  /**
   * Initialize the first auto-selected logo (parse threads, render on canvas)
   */
  async function initializeFirstLogo(location, logoId, logoUrl) {
    // Skip if Hide Logo setting is enabled
    if (shouldHideLogo()) {
      return;
    }

    // Skip if this location is not selected for customization
    if (!state.selectedLocations.includes(location.id)) {
      return;
    }

    // Parse thread layers from SVG
    await parseLogoThreadLayers(location, logoId, logoUrl);

    // Render logo on canvas
    renderLogoOnCanvas(location, logoId, logoUrl);

    // Render thread layers panel
    renderThreadLayers();

    // Update Logo tab view if it's active
    if (state.activeTab === 'logo') {
      renderLogoInLogoView();
    }
  }

  // ============================================
  // THREAD LAYERS
  // ============================================
  function renderThreadLayers() {
    // Check if Recolor setting is enabled
    if (!hasLogoSetting('Recolor')) {
      elements.threadLayers.innerHTML = '';
      return;
    }

    const location = getActiveLocation();
    const locState = location ? state.locationState[location.id] : null;
    const selectedLogo = locState?.selectedLogo;

    // If user selected "no logo", clear thread layers
    if (locState?.noLogo) {
      elements.threadLayers.innerHTML = '';
      return;
    }

    if (!location || !selectedLogo) {
      elements.threadLayers.innerHTML = '<p class="cv2-placeholder">Select a logo to customize</p>';
      return;
    }

    const logoId = selectedLogo.id;

    // Get thread layers parsed from the actual SVG (per-logo)
    const svgThreadLayers = locState.svgThreadLayers[logoId] || [];

    if (svgThreadLayers.length === 0) {
      elements.threadLayers.innerHTML = '<p class="cv2-placeholder">No thread layers in this logo</p>';
      return;
    }

    // Get custom colors from state for this specific logo
    const customColors = locState.logoThreadColors[logoId] || {};

    // Build the thread layers HTML using parsed SVG layers
    const layersHtml = svgThreadLayers.map(layer => {
      const { index, defaultHex, name, code, isFixedHex, isEditable } = layer;

      // Check if this layer is disabled (fixed hex that's not editable)
      const isDisabled = isFixedHex && !isEditable;

      // Check if user has customized this thread color
      const customThread = customColors[index];
      const displayHex = customThread?.hex || defaultHex;
      const displayName = customThread?.name || name;
      const displayCode = customThread?.code || code;

      // Ensure hex has # prefix
      const bgColor = displayHex.startsWith('#') ? displayHex : `#${displayHex}`;

      const isPickerOpen = state.activeThreadPicker === index;

      // Add disabled class and attributes for non-editable layers
      const disabledClass = isDisabled ? 'cv2-thread-layer--disabled' : '';
      const disabledAttr = isDisabled ? 'disabled' : '';
      const swatchTitle = isDisabled ? 'This color cannot be changed' : 'Click to change color';

      return `
        <div class="cv2-thread-layer ${disabledClass}">
          <div class="cv2-thread-picker">
            <button
              class="cv2-thread-swatch"
              style="background-color: ${bgColor}"
              data-thread-index="${index}"
              title="${swatchTitle}"
              ${disabledAttr}
            ></button>
            ${!isDisabled ? `
            <div class="cv2-thread-picker-menu ${isPickerOpen ? 'cv2-thread-picker-menu--open' : ''}" data-picker-index="${index}">
              ${renderThreadColorOptions(index, displayHex)}
            </div>
            ` : ''}
          </div>
          <div class="cv2-thread-info">
            <div class="cv2-thread-name">${displayName}</div>
            ${displayCode ? `<div class="cv2-thread-code">${displayCode}</div>` : ''}
          </div>
        </div>
      `;
    }).join('');

    elements.threadLayers.innerHTML = layersHtml;

    // Bind swatch click events
    elements.threadLayers.querySelectorAll('.cv2-thread-swatch').forEach(swatch => {
      swatch.addEventListener('click', handleThreadSwatchClick);
    });

    // Bind color option click events
    elements.threadLayers.querySelectorAll('.cv2-thread-picker-item').forEach(item => {
      item.addEventListener('click', handleThreadColorSelect);
    });

    // Bind search input events
    elements.threadLayers.querySelectorAll('.cv2-thread-search-input').forEach(input => {
      input.addEventListener('input', handleThreadSearchInput);
      input.addEventListener('click', (e) => e.stopPropagation()); // Prevent closing picker
    });

    // Bind scroll event for lazy loading
    elements.threadLayers.querySelectorAll('.cv2-thread-picker-list').forEach(list => {
      list.addEventListener('scroll', handleThreadPickerScroll);
    });

    // Focus the search input if picker is open
    if (state.activeThreadPicker !== null) {
      const searchInput = elements.threadLayers.querySelector('.cv2-thread-picker-menu--open .cv2-thread-search-input');
      if (searchInput && document.activeElement !== searchInput) {
        // Only focus if not already focused (preserve cursor position)
        setTimeout(() => searchInput.focus(), 0);
      }
    }
  }

  // ============================================
  // PERSONALIZATION
  // ============================================
  function renderPersonalizeContent() {
    const location = getActiveLocation();
    const locState = location ? state.locationState[location.id] : null;

    if (!location) {
      elements.personalizeContent.innerHTML = '<p class="cv2-placeholder">Select a location first</p>';
      return;
    }

    // Check feature settings
    const canResize = hasPersonalizationSetting('Resize');
    const canRecolor = hasPersonalizationSetting('Recolor');
    const canFont = hasPersonalizationSetting('Font');
    const canAlign = hasPersonalizationSetting('Align');

    const personalization = locState?.personalization || {};
    const threadColor = personalization.threadColor || getDefaultThreadColor();
    // Persist resolved default to state so it's saved correctly to _customizations
    if (!personalization.threadColor && locState?.personalization) {
      locState.personalization.threadColor = threadColor;
    }
    const isPickerOpen = state.activeTextThreadPicker;

    // Font dropdown - only show if Font setting enabled AND multiple fonts available
    const availableFonts = getPersonalizationFonts();
    const selectedFont = personalization.font || getDefaultFont();
    const showFontDropdown = canFont && availableFonts.length > 1;
    const isFontDropdownOpen = state.fontDropdownOpen;

    // Text alignment
    const textAlign = personalization.textAlign || 'center';

    // Text resizing toggle - only relevant if Resize setting enabled
    const enableTextResizing = canResize && (personalization.enableTextResizing !== false);

    const html = `
      <div class="cv2-personalize-section">
        <label class="cv2-label">Personalization Text</label>
        <textarea
          class="cv2-text-input"
          id="cv2-text-input"
          placeholder="Enter text..."
          rows="2"
          maxlength="77"
        >${personalization.text || ''}</textarea>
        <p class="cv2-char-hint">Max 25 characters per line, 3 lines</p>
        <p class="cv2-char-error" id="cv2-char-error" style="display: none; color: #c00;"></p>
        ${canResize ? `
        <label class="cv2-resize-checkbox">
          <input type="checkbox" id="cv2-enable-text-resize" ${enableTextResizing ? 'checked' : ''}>
          <span class="cv2-resize-checkbox-box">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="cv2-resize-checkbox-label">Enable text resizing</span>
        </label>
        ` : ''}
      </div>

      ${showFontDropdown ? `
      <div class="cv2-personalize-section">
        <label class="cv2-label">Font</label>
        <div class="cv2-font-dropdown">
          <button class="cv2-font-dropdown-trigger" id="cv2-font-dropdown-trigger" type="button">
            <span class="cv2-font-preview" style="font-family: '${selectedFont.family}', sans-serif;">${selectedFont.name}</span>
            <svg class="cv2-font-dropdown-arrow ${isFontDropdownOpen ? 'cv2-font-dropdown-arrow--open' : ''}" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="cv2-font-dropdown-menu ${isFontDropdownOpen ? 'cv2-font-dropdown-menu--open' : ''}" id="cv2-font-dropdown-menu">
            ${availableFonts.map(font => `
              <div
                class="cv2-font-dropdown-item ${font.id === selectedFont.id ? 'cv2-font-dropdown-item--selected' : ''}"
                data-font-id="${font.id}"
                data-font-name="${font.name}"
                data-font-family="${font.family}"
                data-font-type="${font.type}"
              >
                <span class="cv2-font-dropdown-item-name" style="font-family: '${font.family}', sans-serif;">${font.name}</span>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
      ` : ''}

      ${canAlign ? `
      <div class="cv2-personalize-section">
        <label class="cv2-label">Alignment</label>
        <div class="cv2-alignment-buttons">
          <button class="cv2-alignment-btn ${textAlign === 'left' ? 'cv2-alignment-btn--active' : ''}" data-align="left" type="button" title="Align Left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 3h12M2 6h8M2 9h10M2 12h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <button class="cv2-alignment-btn ${textAlign === 'center' ? 'cv2-alignment-btn--active' : ''}" data-align="center" type="button" title="Align Center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 3h12M4 6h8M3 9h10M5 12h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <button class="cv2-alignment-btn ${textAlign === 'right' ? 'cv2-alignment-btn--active' : ''}" data-align="right" type="button" title="Align Right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 3h12M6 6h8M4 9h10M8 12h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
      ` : ''}

      ${canRecolor ? `
      <div class="cv2-personalize-section">
        <label class="cv2-label">Thread Color</label>
        <div class="cv2-text-thread-picker">
          <button
            class="cv2-text-thread-swatch"
            id="cv2-text-thread-swatch"
            style="background-color: ${threadColor.hex}"
            title="Click to change color"
          ></button>
          <div class="cv2-text-thread-info">
            <span class="cv2-text-thread-name">${threadColor.name}</span>
            ${threadColor.code ? `<span class="cv2-text-thread-code">${threadColor.code}</span>` : ''}
          </div>
          <div class="cv2-text-thread-picker-menu ${isPickerOpen ? 'cv2-text-thread-picker-menu--open' : ''}" id="cv2-text-thread-picker-menu">
            ${renderTextThreadColorOptions(threadColor.hex)}
          </div>
        </div>
      </div>
      ` : ''}
    `;

    elements.personalizeContent.innerHTML = html;

    // Bind events
    const textInput = document.getElementById('cv2-text-input');
    if (textInput) {
      textInput.addEventListener('input', handleTextInput);
      textInput.addEventListener('keypress', handleTextKeypress);
    }

    const resizeCheckbox = document.getElementById('cv2-enable-text-resize');
    if (resizeCheckbox) {
      resizeCheckbox.addEventListener('change', handleTextResizeToggle);
    }

    const threadSwatch = document.getElementById('cv2-text-thread-swatch');
    if (threadSwatch) {
      threadSwatch.addEventListener('click', handleTextThreadSwatchClick);
    }

    // Bind font dropdown events
    const fontDropdownTrigger = document.getElementById('cv2-font-dropdown-trigger');
    if (fontDropdownTrigger) {
      fontDropdownTrigger.addEventListener('click', handleFontDropdownToggle);
    }

    // Bind font option click events
    elements.personalizeContent.querySelectorAll('.cv2-font-dropdown-item').forEach(item => {
      item.addEventListener('click', handleFontSelect);
    });

    // Bind alignment button click events
    elements.personalizeContent.querySelectorAll('.cv2-alignment-btn').forEach(btn => {
      btn.addEventListener('click', handleTextAlignmentSelect);
    });

    // Bind color option click events
    elements.personalizeContent.querySelectorAll('.cv2-text-thread-picker-item').forEach(item => {
      item.addEventListener('click', handleTextThreadColorSelect);
    });

    // Bind search input events
    const searchInput = elements.personalizeContent.querySelector('.cv2-text-thread-search-input');
    if (searchInput) {
      searchInput.addEventListener('input', handleTextThreadSearchInput);
      searchInput.addEventListener('click', (e) => e.stopPropagation());
    }

    // Bind scroll event for lazy loading
    const pickerList = elements.personalizeContent.querySelector('.cv2-text-thread-picker-list');
    if (pickerList) {
      pickerList.addEventListener('scroll', handleTextThreadPickerScroll);
    }

    // Focus the search input if picker is open
    if (state.activeTextThreadPicker) {
      const searchInputEl = elements.personalizeContent.querySelector('.cv2-text-thread-search-input');
      if (searchInputEl && document.activeElement !== searchInputEl) {
        setTimeout(() => searchInputEl.focus(), 0);
      }
    }
  }

  /**
   * Unified thread color picker renderer
   * @param {Object} config
   * @param {string} config.type - 'logo' | 'text'
   * @param {string} config.currentHex - Currently selected color
   * @param {number} [config.threadIndex] - For logo pickers, the thread layer index
   */
  function renderColorPickerOptions({ type, currentHex, threadIndex = null }) {
    const isLogo = type === 'logo';
    const prefix = isLogo ? 'cv2-thread' : 'cv2-text-thread';
    const searchQuery = isLogo ? state.threadSearchQuery : state.textThreadSearchQuery;
    const loadedCount = isLogo ? state.threadPickerLoadedCount : state.textThreadPickerLoadedCount;
    const indexAttr = threadIndex !== null ? ` data-thread-index="${threadIndex}"` : '';

    const allColors = getThreadColorOptions();
    const query = searchQuery.toLowerCase().trim();

    // Get recommended colors from location's threadInstructions
    const location = getActiveLocation();
    const threadInstructions = location?.threadInstructions || [];
    const recommendedColors = parseRecommendedColors(threadInstructions);

    // Filter colors by search query or use lazy loading
    let colors;
    let hasMore = false;
    let filteredRecommended = [];

    if (query) {
      // When searching, filter both recommended and all colors
      filteredRecommended = recommendedColors.filter(color =>
        color.name.toLowerCase().includes(query) ||
        color.code.toLowerCase().includes(query)
      );
      colors = allColors.filter(color =>
        color.name.toLowerCase().includes(query) ||
        color.code.toLowerCase().includes(query)
      );
    } else {
      // No search - show all recommended and lazy load the rest
      filteredRecommended = recommendedColors;
      colors = allColors.slice(0, loadedCount);
      hasMore = allColors.length > loadedCount;
    }

    // Build search input
    const searchHtml = `
      <div class="${prefix}-search">
        <input
          type="text"
          class="${prefix}-search-input"
          placeholder="Search by name or code..."
          value="${searchQuery}"${indexAttr}
        />
      </div>
    `;

    // Helper to render a color item
    const renderColorItem = (color) => {
      const isSelected = color.hex.toLowerCase() === currentHex.toLowerCase() ||
                         `#${color.hex}`.toLowerCase() === currentHex.toLowerCase();
      const bgColor = color.hex.startsWith('#') ? color.hex : `#${color.hex}`;

      return `
        <div
          class="${prefix}-picker-item ${isSelected ? `${prefix}-picker-item--selected` : ''}"${indexAttr}
          data-hex="${color.hex}"
          data-name="${color.name}"
          data-code="${color.code || ''}"
        >
          <span class="${prefix}-picker-swatch" style="background-color: ${bgColor}"></span>
          <span class="${prefix}-picker-text">${color.name}</span>
          ${color.code ? `<span class="${prefix}-picker-code">${color.code}</span>` : ''}
        </div>
      `;
    };

    // Build color options
    let optionsHtml = '';

    // Add recommended colors section if there are any
    if (filteredRecommended.length > 0) {
      optionsHtml += `<div class="cv2-thread-picker-section-label">Recommended</div>`;
      optionsHtml += filteredRecommended.map(renderColorItem).join('');
      optionsHtml += `<div class="cv2-thread-picker-divider"></div>`;
      optionsHtml += `<div class="cv2-thread-picker-section-label">All Colors</div>`;
    }

    if (colors.length > 0) {
      optionsHtml += colors.map(renderColorItem).join('');

      if (hasMore) {
        optionsHtml += `<div class="${prefix}-picker-loading"${indexAttr}>Scroll for more...</div>`;
      }
    } else if (filteredRecommended.length === 0) {
      optionsHtml = `<div class="${prefix}-picker-empty">No threads found</div>`;
    }

    return searchHtml + `<div class="${prefix}-picker-list"${indexAttr}>` + optionsHtml + '</div>';
  }

  // Legacy wrapper for text thread color picker
  function renderTextThreadColorOptions(currentHex) {
    return renderColorPickerOptions({ type: 'text', currentHex });
  }

  function handleTextInput(e) {
    const value = e.target.value;
    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];

    // Validate and store text
    const validation = validatePersonalizationText(value);
    const errorEl = document.getElementById('cv2-char-error');

    if (!validation.valid) {
      if (errorEl) {
        errorEl.textContent = validation.error;
        errorEl.style.display = 'block';
      }
    } else {
      if (errorEl) {
        errorEl.style.display = 'none';
      }
    }

    // Update state
    locState.personalization.text = value;

    // Reset position when text is cleared - new text should use default placement
    if (!value.trim()) {
      locState.personalization.position = null;
    }

    // Update text on canvas
    renderTextOnCanvas();

    // Update add-to-cart button state (noLogo requires text)
    updateAddToCartButton();

    // Auto-select text object on canvas when user starts typing
    if (value.trim() && state.fabricCanvas) {
      const textObj = state.fabricCanvas.getObjects().find(obj => obj.isText);
      if (textObj) {
        state.fabricCanvas.setActiveObject(textObj);
        state.fabricCanvas.requestRenderAll();
      }
    }
  }

  function handleTextKeypress(e) {
    const value = e.target.value;
    const lines = value.split('\n');
    const maxLines = 3;
    const maxCharsPerLine = 25;

    // Prevent adding more lines if at max
    if (e.key === 'Enter' && lines.length >= maxLines) {
      e.preventDefault();
      const errorEl = document.getElementById('cv2-char-error');
      if (errorEl) {
        errorEl.textContent = `Maximum ${maxLines} lines allowed.`;
        errorEl.style.display = 'block';
      }
      return;
    }

    // Check current line length
    const cursorPos = e.target.selectionStart;
    let charCount = 0;
    let currentLineIndex = 0;
    for (let i = 0; i < lines.length; i++) {
      if (charCount + lines[i].length >= cursorPos) {
        currentLineIndex = i;
        break;
      }
      charCount += lines[i].length + 1; // +1 for newline
    }

    const currentLine = lines[currentLineIndex] || '';
    if (currentLine.length >= maxCharsPerLine && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Enter') {
      // Allow if selecting text to replace
      if (e.target.selectionStart === e.target.selectionEnd) {
        e.preventDefault();
        const errorEl = document.getElementById('cv2-char-error');
        if (errorEl) {
          errorEl.textContent = `Maximum ${maxCharsPerLine} characters per line allowed.`;
          errorEl.style.display = 'block';
        }
      }
    }
  }

  function handleTextResizeToggle(e) {
    const locationId = state.activeLocation;
    if (!locationId) return;

    const locState = state.locationState[locationId];
    if (!locState) return;

    locState.personalization.enableTextResizing = e.target.checked;

    // Update existing text object on canvas if present
    updateTextObjectControls();
  }

  function updateTextObjectControls() {
    if (!state.fabricCanvas) return;

    const locationId = state.activeLocation;
    const locState = state.locationState[locationId];
    const enableResizing = locState?.personalization?.enableTextResizing !== false;

    const textObj = state.fabricCanvas.getObjects().find(obj => obj.isText);
    if (textObj) {
      textObj.set({
        hasControls: enableResizing,
        lockScalingX: !enableResizing,
        lockScalingY: !enableResizing
      });
      state.fabricCanvas.requestRenderAll();
    }
  }

  function validatePersonalizationText(text) {
    const lines = text.split('\n');
    const maxLines = 3;
    const maxCharsPerLine = 25;

    if (lines.length > maxLines) {
      return { valid: false, error: `Maximum ${maxLines} lines allowed.` };
    }

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length > maxCharsPerLine) {
        return { valid: false, error: `Line ${i + 1} exceeds ${maxCharsPerLine} characters.` };
      }
    }

    return { valid: true };
  }

  // ============================================
  // FONT DROPDOWN HANDLERS
  // ============================================
  function handleFontDropdownToggle(e) {
    e.stopPropagation();

    // Close text thread picker if open
    if (state.activeTextThreadPicker) {
      state.activeTextThreadPicker = false;
    }

    // Toggle font dropdown
    state.fontDropdownOpen = !state.fontDropdownOpen;
    renderPersonalizeContent();
  }

  function handleFontSelect(e) {
    e.stopPropagation();
    const item = e.currentTarget;
    const fontId = item.dataset.fontId;
    const fontName = item.dataset.fontName;
    const fontFamily = item.dataset.fontFamily;
    const fontType = item.dataset.fontType;

    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];

    // Update state with selected font
    locState.personalization.font = {
      id: fontId,
      name: fontName,
      family: fontFamily,
      type: fontType
    };

    // Close dropdown
    state.fontDropdownOpen = false;

    // Clear Fabric font cache and re-render text with new font
    fabric.util.clearFabricFontCache();
    renderTextOnCanvas();

    // Auto-select text object on canvas after font change
    if (state.fabricCanvas) {
      const textObj = state.fabricCanvas.getObjects().find(obj => obj.isText);
      if (textObj) {
        state.fabricCanvas.setActiveObject(textObj);
        state.fabricCanvas.requestRenderAll();
      }
    }

    renderPersonalizeContent();
  }

  function handleTextAlignmentSelect(e) {
    e.stopPropagation();
    const btn = e.currentTarget;
    const alignment = btn.dataset.align;

    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];

    // Update state with selected alignment
    locState.personalization.textAlign = alignment;

    // Re-render text with new alignment
    renderTextOnCanvas();

    // Auto-select text object on canvas after alignment change
    if (state.fabricCanvas) {
      const textObj = state.fabricCanvas.getObjects().find(obj => obj.isText);
      if (textObj) {
        state.fabricCanvas.setActiveObject(textObj);
        state.fabricCanvas.requestRenderAll();
      }
    }

    renderPersonalizeContent();
  }

  function handleTextThreadSwatchClick(e) {
    e.stopPropagation();

    // Close font dropdown if open
    if (state.fontDropdownOpen) {
      state.fontDropdownOpen = false;
    }

    // Toggle picker
    state.activeTextThreadPicker = !state.activeTextThreadPicker;
    if (state.activeTextThreadPicker) {
      state.textThreadSearchQuery = '';
      state.textThreadPickerLoadedCount = 50;
    }

    renderPersonalizeContent();
  }

  function handleTextThreadColorSelect(e) {
    e.stopPropagation();
    const item = e.currentTarget;
    const hex = item.dataset.hex;
    const name = item.dataset.name;
    const code = item.dataset.code;

    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];

    // Update state
    locState.personalization.threadColor = { hex, name, code };

    // Close picker
    state.activeTextThreadPicker = false;
    state.textThreadSearchQuery = '';

    // Update canvas
    renderTextOnCanvas();

    // Auto-select text object on canvas after thread color change
    if (state.fabricCanvas) {
      const textObj = state.fabricCanvas.getObjects().find(obj => obj.isText);
      if (textObj) {
        state.fabricCanvas.setActiveObject(textObj);
        state.fabricCanvas.requestRenderAll();
      }
    }

    renderPersonalizeContent();
  }

  function handleTextThreadSearchInput(e) {
    e.stopPropagation();
    state.textThreadSearchQuery = e.target.value;

    // Re-render picker content
    const location = getActiveLocation();
    const locState = location ? state.locationState[location.id] : null;
    const threadColor = locState?.personalization?.threadColor || { hex: '#FFFFFF' };

    const pickerMenu = document.getElementById('cv2-text-thread-picker-menu');
    if (pickerMenu) {
      pickerMenu.innerHTML = renderTextThreadColorOptions(threadColor.hex);

      // Re-bind events
      pickerMenu.querySelectorAll('.cv2-text-thread-picker-item').forEach(item => {
        item.addEventListener('click', handleTextThreadColorSelect);
      });
      const searchInput = pickerMenu.querySelector('.cv2-text-thread-search-input');
      if (searchInput) {
        searchInput.addEventListener('input', handleTextThreadSearchInput);
        searchInput.addEventListener('click', (e) => e.stopPropagation());
        searchInput.focus();
        searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
      }
    }
  }

  function handleTextThreadPickerScroll(e) {
    const list = e.target;

    // Don't lazy load if searching
    if (state.textThreadSearchQuery.trim()) return;

    // Check if scrolled near bottom
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 50;

    if (nearBottom) {
      const allColors = getThreadColorOptions();
      const currentCount = state.textThreadPickerLoadedCount;

      if (currentCount < allColors.length) {
        state.textThreadPickerLoadedCount = Math.min(currentCount + 50, allColors.length);

        const scrollTop = list.scrollTop;
        const location = getActiveLocation();
        const locState = location ? state.locationState[location.id] : null;
        const threadColor = locState?.personalization?.threadColor || { hex: '#FFFFFF' };

        const pickerMenu = document.getElementById('cv2-text-thread-picker-menu');
        if (pickerMenu) {
          pickerMenu.innerHTML = renderTextThreadColorOptions(threadColor.hex);

          // Re-bind events
          pickerMenu.querySelectorAll('.cv2-text-thread-picker-item').forEach(item => {
            item.addEventListener('click', handleTextThreadColorSelect);
          });
          const searchInput = pickerMenu.querySelector('.cv2-text-thread-search-input');
          if (searchInput) {
            searchInput.addEventListener('input', handleTextThreadSearchInput);
            searchInput.addEventListener('click', (e) => e.stopPropagation());
          }
          const newList = pickerMenu.querySelector('.cv2-text-thread-picker-list');
          if (newList) {
            newList.addEventListener('scroll', handleTextThreadPickerScroll);
            newList.scrollTop = scrollTop;
          }
        }
      }
    }
  }

  function handleThreadSearchInput(e) {
    e.stopPropagation();
    state.threadSearchQuery = e.target.value;

    // Re-render just the picker content (not the whole thread layers)
    const threadIndex = parseInt(e.target.dataset.threadIndex);
    const location = getActiveLocation();
    const locState = location ? state.locationState[location.id] : null;
    const selectedLogo = locState?.selectedLogo;

    if (!selectedLogo) return;

    const logoId = selectedLogo.id;
    const svgThreadLayers = locState.svgThreadLayers[logoId] || [];
    const layer = svgThreadLayers.find(l => l.index === threadIndex);
    if (!layer) return;

    const customColors = locState.logoThreadColors[logoId] || {};
    const customThread = customColors[threadIndex];
    const displayHex = customThread?.hex || layer.defaultHex;

    // Update picker content
    const pickerMenu = elements.threadLayers.querySelector(`[data-picker-index="${threadIndex}"]`);
    if (pickerMenu) {
      pickerMenu.innerHTML = renderThreadColorOptions(threadIndex, displayHex);

      // Re-bind events for the new content
      pickerMenu.querySelectorAll('.cv2-thread-picker-item').forEach(item => {
        item.addEventListener('click', handleThreadColorSelect);
      });
      pickerMenu.querySelectorAll('.cv2-thread-search-input').forEach(input => {
        input.addEventListener('input', handleThreadSearchInput);
        input.addEventListener('click', (e) => e.stopPropagation());
        // Restore focus and cursor position
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      });
    }
  }

  function handleThreadPickerScroll(e) {
    const list = e.target;
    const threadIndex = parseInt(list.dataset.threadIndex);

    // Don't lazy load if searching (all results shown)
    if (state.threadSearchQuery.trim()) return;

    // Check if scrolled near bottom (within 50px)
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 50;

    if (nearBottom) {
      const allColors = getThreadColorOptions();
      const currentCount = state.threadPickerLoadedCount;

      // If there are more colors to load
      if (currentCount < allColors.length) {
        // Load 50 more
        state.threadPickerLoadedCount = Math.min(currentCount + 50, allColors.length);

        // Get current scroll position
        const scrollTop = list.scrollTop;

        // Get current hex for selected indication
        const location = getActiveLocation();
        const locState = location ? state.locationState[location.id] : null;
        const selectedLogo = locState?.selectedLogo;
        const logoId = selectedLogo?.id;
        const svgThreadLayers = locState?.svgThreadLayers?.[logoId] || [];
        const layer = svgThreadLayers.find(l => l.index === threadIndex);
        const customColors = locState?.logoThreadColors?.[logoId] || {};
        const customThread = customColors[threadIndex];
        const displayHex = customThread?.hex || layer?.defaultHex || '#000000';

        // Update just the picker content
        const pickerMenu = elements.threadLayers.querySelector(`[data-picker-index="${threadIndex}"]`);
        if (pickerMenu) {
          pickerMenu.innerHTML = renderThreadColorOptions(threadIndex, displayHex);

          // Re-bind events for the new content
          pickerMenu.querySelectorAll('.cv2-thread-picker-item').forEach(item => {
            item.addEventListener('click', handleThreadColorSelect);
          });
          pickerMenu.querySelectorAll('.cv2-thread-search-input').forEach(input => {
            input.addEventListener('input', handleThreadSearchInput);
            input.addEventListener('click', (e) => e.stopPropagation());
          });
          pickerMenu.querySelectorAll('.cv2-thread-picker-list').forEach(newList => {
            newList.addEventListener('scroll', handleThreadPickerScroll);
            // Restore scroll position
            newList.scrollTop = scrollTop;
          });
        }
      }
    }
  }

  // Legacy wrapper for logo thread color picker
  function renderThreadColorOptions(threadIndex, currentHex) {
    return renderColorPickerOptions({ type: 'logo', currentHex, threadIndex });
  }

  /**
   * Parse thread instructions into recommended color objects
   * Handles multiple data formats from Shopify metafields
   */
  function parseRecommendedColors(threadInstructions) {
    const recommendedColors = [];

    if (!threadInstructions) return recommendedColors;

    // Handle if threadInstructions is an object with numeric keys (from Shopify metafield)
    const instructions = Array.isArray(threadInstructions)
      ? threadInstructions
      : Object.values(threadInstructions);

    instructions.forEach(thread => {
      if (!thread) return;
      let hex, name, code;

      // Format 1: Array [hex, name, code]
      if (Array.isArray(thread)) {
        [hex, name, code] = thread;
      }
      // Format 2: Object with hex property
      else if (typeof thread === 'object' && thread.hex) {
        hex = thread.hex;
        name = thread.name;
        code = thread.code;
      }
      // Format 3: Array of arrays [[hex, name, code], ...]
      else if (typeof thread === 'object' && thread[0]) {
        hex = thread[0];
        name = thread[1];
        code = thread[2];
      }

      if (hex) {
        // Look up in thread library for name/code if not provided
        const normalizedHex = hex.toLowerCase().replace('#', '');
        if (window.threadLookup) {
          const threadFromLibrary = window.threadLookup[normalizedHex] ||
                                    window.threadLookup['#' + normalizedHex];
          if (threadFromLibrary) {
            if (!name) name = threadFromLibrary.name;
            if (!code) code = threadFromLibrary.code;
          }
        }

        recommendedColors.push({
          hex: hex.startsWith('#') ? hex : `#${hex}`,
          name: name || 'Unknown',
          code: code || ''
        });
      }
    });

    return recommendedColors;
  }

  function resolveLocationLogos(location) {
    let logos = location.logos || [];
    if (typeof logos === 'string') {
      logos = logos.split(',').map(s => s.trim()).filter(Boolean);
    }
    // If Liquid couldn't resolve logos, try JS-based storefrontLogoLists
    if (logos.length === 0 && location.logoListRef && window.storefrontLogoLists) {
      const listMatch = location.logoListRef.match(/list_(\d+)/);
      if (listMatch) {
        const listIndex = parseInt(listMatch[1]) - 1;
        if (window.storefrontLogoLists[listIndex]) {
          logos = window.storefrontLogoLists[listIndex].split(',').map(id => id.trim()).filter(Boolean);
        }
      }
    }
    return logos;
  }

  function getThreadColorOptions() {
    // Use thread library from thread-library-loader.liquid
    if (window.threadLookup && Object.keys(window.threadLookup).length > 0) {
      // Convert hex-keyed object to array
      return Object.values(window.threadLookup).map(thread => ({
        hex: thread.hex.startsWith('#') ? thread.hex : `#${thread.hex}`,
        name: thread.name,
        code: thread.code
      }));
    }

    // Fallback: Check if thread colors are provided in CustomizerV2Data
    if (window.CustomizerV2Data?.threadColors && Array.isArray(window.CustomizerV2Data.threadColors)) {
      return window.CustomizerV2Data.threadColors;
    }

    // Fallback thread color palette
    return [
      { hex: '#FFFFFF', name: 'White', code: '0800' },
      { hex: '#000000', name: 'Black', code: '0020' },
      { hex: '#808080', name: 'Gray', code: '0142' },
      { hex: '#FF0000', name: 'Red', code: '0700' },
      { hex: '#000080', name: 'Navy', code: '0810' },
      { hex: '#006400', name: 'Green', code: '0900' },
      { hex: '#FFD700', name: 'Gold', code: '0600' }
    ];
  }

  /**
   * Get default thread color for personalization
   * Uses first recommended color from active location, then first from thread library
   * @param {Object} [location] - Optional location object (if not provided, uses active location)
   * @returns {Object} { hex, name, code }
   */
  function getDefaultThreadColor(location) {
    // Try recommended colors from location's threadInstructions
    const loc = location || getActiveLocation();
    if (loc?.threadInstructions) {
      const recommended = parseRecommendedColors(loc.threadInstructions);
      if (recommended.length > 0) {
        return recommended[0];
      }
    }

    // Fall back to first color from thread library
    const allColors = getThreadColorOptions();
    if (allColors.length > 0) {
      return allColors[0];
    }

    // Ultimate fallback
    return { hex: '#FFFFFF', name: 'White', code: '0800' };
  }

  function handleThreadSwatchClick(e) {
    e.stopPropagation();
    const threadIndex = parseInt(e.currentTarget.dataset.threadIndex);

    // Toggle picker
    if (state.activeThreadPicker === threadIndex) {
      state.activeThreadPicker = null;
      state.threadSearchQuery = ''; // Reset search when closing
    } else {
      state.activeThreadPicker = threadIndex;
      state.threadSearchQuery = ''; // Reset search when opening new picker
      state.threadPickerLoadedCount = 50; // Reset lazy load count when opening
    }

    renderThreadLayers();
  }

  function handleThreadColorSelect(e) {
    e.stopPropagation();
    const item = e.currentTarget;
    const threadIndex = parseInt(item.dataset.threadIndex);
    const hex = item.dataset.hex;
    const name = item.dataset.name;
    const code = item.dataset.code;

    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];
    const selectedLogo = locState?.selectedLogo;
    if (!selectedLogo) return;

    const logoId = selectedLogo.id;

    // Initialize per-logo color storage if needed
    if (!locState.logoThreadColors[logoId]) {
      locState.logoThreadColors[logoId] = {};
    }

    // Update state with new thread color for this specific logo
    locState.logoThreadColors[logoId][threadIndex] = {
      hex: hex,
      name: name,
      code: code
    };

    // Close picker and reset search
    state.activeThreadPicker = null;
    state.threadSearchQuery = '';

    // Re-render thread layers panel
    renderThreadLayers();

    // Update only this logo's thumbnail in gallery
    updateLogoThumbnail(logoId);

    // Update canvas logo
    renderActiveLocationLogo();

    // Update Logo tab view if active
    if (state.activeTab === 'logo') {
      renderLogoInLogoView();
    }
  }

  /**
   * Update a single logo thumbnail in the gallery with its custom colors
   */
  async function updateLogoThumbnail(logoId) {
    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];
    const customColors = locState.logoThreadColors[logoId] || {};
    const threadLayers = locState.svgThreadLayers?.[logoId] || null;

    const container = document.querySelector(`[data-logo-container="${logoId}"]`);
    if (!container) return;

    const storefront = window.CustomizerV2Data.storefront;
    const baseUrl = window.CustomizerV2Data.logoStorageUrl;
    const logoUrl = `${baseUrl}/${storefront}.${logoId}.svg`;

    try {
      const response = await fetch(logoUrl);
      if (response.ok) {
        let svgText = await response.text();
        svgText = applyThreadColors(svgText, location.threadInstructions, location.switchy, customColors, threadLayers);
        container.innerHTML = svgText;

        const svg = container.querySelector('svg');
        if (svg) {
          svg.setAttribute('width', '100%');
          svg.setAttribute('height', '100%');
          svg.style.maxWidth = '100%';
          svg.style.maxHeight = '100%';
        }
      }
    } catch (err) {
      // Silent fail - logo thumbnail update failed
    }
  }

  // Close thread picker when clicking outside
  function handleThreadPickerOutsideClick(e) {
    // Close logo thread picker
    if (state.activeThreadPicker !== null) {
      const picker = elements.threadLayers.querySelector('.cv2-thread-picker-menu--open');
      if (picker && !picker.contains(e.target) && !e.target.classList.contains('cv2-thread-swatch')) {
        state.activeThreadPicker = null;
        state.threadSearchQuery = ''; // Reset search when closing
        renderThreadLayers();
      }
    }

    // Close text thread picker
    if (state.activeTextThreadPicker) {
      const textPicker = document.getElementById('cv2-text-thread-picker-menu');
      const textSwatch = document.getElementById('cv2-text-thread-swatch');
      if (textPicker && !textPicker.contains(e.target) && e.target !== textSwatch) {
        state.activeTextThreadPicker = false;
        state.textThreadSearchQuery = '';
        renderPersonalizeContent();
      }
    }

    // Close font dropdown
    if (state.fontDropdownOpen) {
      const fontDropdown = document.getElementById('cv2-font-dropdown-menu');
      const fontTrigger = document.getElementById('cv2-font-dropdown-trigger');
      if (fontDropdown && !fontDropdown.contains(e.target) && !fontTrigger?.contains(e.target)) {
        state.fontDropdownOpen = false;
        renderPersonalizeContent();
      }
    }
  }

  // ============================================
  // LOGO LOADING
  // ============================================
  async function loadLogoThumbnails(logos) {
    const storefront = window.CustomizerV2Data.storefront;
    const baseUrl = window.CustomizerV2Data.logoStorageUrl;
    const location = getActiveLocation();
    const locState = location ? state.locationState[location.id] : null;

    for (const logoId of logos) {
      const logoUrl = `${baseUrl}/${storefront}.${logoId}.svg`;
      const container = document.querySelector(`[data-logo-container="${logoId}"]`);

      if (!container) continue;

      // Get custom colors for THIS specific logo (not the selected one)
      const customColors = locState?.logoThreadColors?.[logoId] || {};
      const threadLayers = locState?.svgThreadLayers?.[logoId] || null;

      try {
        const response = await fetch(logoUrl);
        if (response.ok) {
          let svgText = await response.text();

          // Apply thread colors to thumbnails (each logo gets its own custom colors)
          if (location) {
            svgText = applyThreadColors(svgText, location.threadInstructions, location.switchy, customColors, threadLayers);
          }

          container.innerHTML = svgText;

          // Make SVG responsive
          const svg = container.querySelector('svg');
          if (svg) {
            svg.setAttribute('width', '100%');
            svg.setAttribute('height', '100%');
            svg.style.maxWidth = '100%';
            svg.style.maxHeight = '100%';
          }
        }
      } catch (err) {
        // Silent fail - logo loading failed
      }
    }
  }

  // ============================================
  // LOGO ON CANVAS (Fabric.js)
  // ============================================
  async function renderLogoOnCanvas(location, logoId, logoUrl) {
    if (!state.fabricCanvas) {
      return false;
    }

    // Skip logo rendering if Hide Logo setting is enabled
    if (shouldHideLogo()) {
      clearFabricLogo();
      return false;
    }

    // Skip logo rendering if user selected "no logo" for this location
    const locationState = state.locationState[location.id];
    if (locationState?.noLogo) {
      clearFabricLogo();
      return false;
    }

    // Skip logo rendering if this location is not selected for customization
    if (!state.selectedLocations.includes(location.id)) {
      clearFabricLogo();
      return false;
    }

    const img = elements.productImage;

    // Clear existing objects (logo) but keep bounding box
    clearFabricLogo();

    // Redraw bounding box on Fabric canvas
    renderBoundingBox(location);

    // Get default placement coordinates
    const defaultPlacement = parseCoords(location.defaultPlacement);
    if (!defaultPlacement) {
      return;
    }

    // Calculate scale factors
    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;
    const originalWidth = img.naturalWidth || 2048;
    const originalHeight = img.naturalHeight || 2048;
    const scaleX = displayWidth / originalWidth;
    const scaleY = displayHeight / originalHeight;

    // Check if we have a saved position for this location
    const locState = state.locationState[location.id];
    const savedPosition = locState?.logoPosition;
    // Get custom colors for THIS specific logo
    const customColors = locState?.logoThreadColors?.[logoId] || {};
    // Get thread layers for fixed hex color support
    const threadLayers = locState?.svgThreadLayers?.[logoId] || null;

    // Scale the placement coordinates (use saved or default)
    let scaledPlacement;
    if (savedPosition) {
      // If saved position has canvas dimensions, scale to current display size
      // This handles loading from cart where position was saved at a different size
      const savedCanvasWidth = savedPosition.canvasWidth || displayWidth;
      const savedCanvasHeight = savedPosition.canvasHeight || displayHeight;
      const positionScaleX = displayWidth / savedCanvasWidth;
      const positionScaleY = displayHeight / savedCanvasHeight;

      scaledPlacement = {
        left: savedPosition.left * positionScaleX,
        top: savedPosition.top * positionScaleY,
        scaleX: savedPosition.scaleX * positionScaleX,
        scaleY: savedPosition.scaleY * positionScaleY
      };
    } else {
      scaledPlacement = {
        left: defaultPlacement.x * scaleX,
        top: defaultPlacement.y * scaleY,
        width: defaultPlacement.width * scaleX,
        height: defaultPlacement.height * scaleY
      };
    }

    try {
      // Fetch the SVG
      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error(`Failed to fetch logo: ${response.status}`);

      let svgText = await response.text();

      // Apply thread colors using logo system universal pattern (including custom colors and fixed hex support)
      svgText = applyThreadColors(svgText, location.threadInstructions, location.switchy, customColors, threadLayers);

      // Set crossOrigin on every nested <image> so the canvas does not taint
      // (Fabric does not propagate crossOrigin to nested images, and a single
      // missing CORS header on a logo texture taints the entire canvas — silently
      // breaking toBlob/toDataURL with no error.)
      svgText = svgText.replace(/<image\b/gi, '<image crossorigin="anonymous"');

      // Load SVG into Fabric.js — wrap callback in a real Promise so callers can await
      await new Promise((resolveLoad) => {
        fabric.loadSVGFromString(svgText, (objects, options) => {
          const logoGroup = fabric.util.groupSVGElements(objects, options);

          // Calculate scale to fit within placement
          let logoScale;
          if (savedPosition) {
            // Use scaled position values (already adjusted for current display size)
            logoScale = scaledPlacement.scaleX || 1;
          } else {
            const scaleToFitWidth = scaledPlacement.width / logoGroup.width;
            const scaleToFitHeight = scaledPlacement.height / logoGroup.height;
            logoScale = Math.min(scaleToFitWidth, scaleToFitHeight);
          }

          // Check feature settings
          const canMove = hasLogoSetting('Move');
          const canResize = hasLogoSetting('Resize');
          const isInteractive = canMove || canResize;

          // Set logo properties (use scaledPlacement for both saved and default positions)
          logoGroup.set({
            left: scaledPlacement.left,
            top: scaledPlacement.top,
            scaleX: logoScale,
            scaleY: logoScale,
            angle: savedPosition?.angle || 0,
            originX: 'left',
            originY: 'top',
            selectable: isInteractive,
            evented: isInteractive,
            hasControls: canResize,
            hasBorders: isInteractive,
            lockMovementX: !canMove,
            lockMovementY: !canMove,
            lockScalingFlip: true, // Prevent flipping when scaling
            uniformScaling: true, // Force uniform scaling (keep aspect ratio)
            cornerColor: '#F6DD5E',
            cornerStyle: 'rect',
            cornerSize: 6,
            transparentCorners: false,
            borderColor: '#F6DD5E',
            borderScaleFactor: 2,
            // Rotation control styling
            centeredRotation: true,
            // Custom property to identify logo objects
            isLogo: true
          });

          // Hide middle controls (only keep corners for uniform scaling)
          logoGroup.setControlsVisibility({
            mt: false, // middle top
            mb: false, // middle bottom
            ml: false, // middle left
            mr: false  // middle right
          });

          // Set rotation handle offset
          if (canResize) {
            logoGroup.controls.mtr.offsetY = -20;
          }

          // Add to canvas, then calculate coordinates
          state.fabricCanvas.add(logoGroup);
          logoGroup.setCoords();
          if (isInteractive) {
            state.fabricCanvas.setActiveObject(logoGroup);
          }
          state.fabricCanvas.renderAll();

          // Save initial position for screenshot service (even if user doesn't move/resize)
          if (!savedPosition) {
            state.locationState[location.id].logoPosition = {
              left: logoGroup.left,
              top: logoGroup.top,
              scaleX: logoGroup.scaleX,
              scaleY: logoGroup.scaleY,
              angle: logoGroup.angle || 0,
              canvasWidth: state.fabricCanvas.width,
              canvasHeight: state.fabricCanvas.height
            };
          }
          resolveLoad();
        });
      });

      return true;
    } catch (err) {
      console.error('[Customizer] renderLogoOnCanvas failed:', err);
      return false;
    }
  }

  function clearFabricLogo() {
    if (!state.fabricCanvas) return;

    // Remove only logo objects, keep bounding box
    const objects = state.fabricCanvas.getObjects();
    objects.forEach(obj => {
      if (obj.isLogo) {
        state.fabricCanvas.remove(obj);
      }
    });
  }

  // ============================================
  // TEXT ON CANVAS (Fabric.js)
  // ============================================
  function renderTextOnCanvas() {
    if (!state.fabricCanvas) {
      return;
    }

    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];
    const personalization = locState?.personalization;
    const text = personalization?.text?.trim();

    // Clear existing text
    clearFabricText();

    // If no text, don't render
    if (!text) {
      state.fabricCanvas.renderAll();
      return;
    }

    const img = elements.productImage;
    if (!img) return;

    // Calculate scale factors
    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;
    const originalWidth = img.naturalWidth || 2048;
    const originalHeight = img.naturalHeight || 2048;
    const scaleX = displayWidth / originalWidth;
    const scaleY = displayHeight / originalHeight;

    // Get bounding box for text placement (personalization canvas > logo canvas > defaultPlacement)
    const canvasCoords = parseCoords(location.personalizationCanvas) || parseCoords(location.canvas) || parseCoords(location.defaultPlacement);
    if (!canvasCoords) {
      return;
    }

    // Scale canvas bounds
    const bounds = {
      x: canvasCoords.x * scaleX,
      y: canvasCoords.y * scaleY,
      width: canvasCoords.width * scaleX,
      height: canvasCoords.height * scaleY
    };

    // Get thread color
    const threadColor = personalization?.threadColor || getDefaultThreadColor();
    const fillColor = threadColor.hex.startsWith('#') ? threadColor.hex : `#${threadColor.hex}`;

    // Get saved position or use default
    const savedPosition = personalization?.position;

    // Scale saved position if canvas dimensions are available
    let scaledPosition = null;
    if (savedPosition && savedPosition.left != null && savedPosition.top != null) {
      const savedCanvasWidth = savedPosition.canvasWidth || displayWidth;
      const savedCanvasHeight = savedPosition.canvasHeight || displayHeight;
      const positionScaleX = displayWidth / savedCanvasWidth;
      const positionScaleY = displayHeight / savedCanvasHeight;

      scaledPosition = {
        left: savedPosition.left * positionScaleX,
        top: savedPosition.top * positionScaleY,
        fontSize: savedPosition.fontSize * Math.min(positionScaleX, positionScaleY),
        angle: savedPosition.angle
      };
    }

    // Get default placement for text (use personalization default placement, fallback to center of bounds)
    const defaultPlacement = parseCoords(location.personalizationDefaultPlacement);
    const defaultLeft = defaultPlacement
      ? (defaultPlacement.x + defaultPlacement.width / 2) * scaleX
      : bounds.x + bounds.width / 2;
    const defaultTop = defaultPlacement
      ? (defaultPlacement.y + defaultPlacement.height / 2) * scaleY
      : bounds.y + bounds.height / 2;

    // Get selected font or use default
    const selectedFont = personalization?.font || getDefaultFont();
    const fontFamily = `'${selectedFont.family}', sans-serif`;

    // Get text alignment
    const textAlign = personalization?.textAlign || 'center';

    // Check feature settings for text
    const canMove = hasPersonalizationSetting('Move');
    const canResize = hasPersonalizationSetting('Resize');
    const isInteractive = canMove || canResize;

    // Get text resizing preference - only if Resize setting enabled AND checkbox checked
    const enableTextResizing = canResize && (personalization?.enableTextResizing !== false);

    // Determine fontSize: use scaled saved fontSize, or use a reasonable default
    // Don't maximize to fill bounds - start with a sensible size like old system
    const DEFAULT_FONT_SIZE = 40;
    let fontSize;
    if (scaledPosition?.fontSize) {
      fontSize = scaledPosition.fontSize;
    } else if (savedPosition?.fontSize) {
      fontSize = savedPosition.fontSize;
    } else {
      // Start with default, don't maximize to fill bounds
      fontSize = DEFAULT_FONT_SIZE;
    }

    // Create Text object with fontSize approach (scale always 1.0)
    // padding: expands selection box to cover glyph overhang that Fabric doesn't measure
    const textObj = new fabric.Text(text, {
      left: scaledPosition?.left ?? savedPosition?.left ?? defaultLeft,
      top: scaledPosition?.top ?? savedPosition?.top ?? defaultTop,
      fontFamily: fontFamily,
      fontSize: fontSize,
      fill: fillColor,
      textAlign: textAlign,
      originX: 'center',
      originY: 'center',
      scaleX: 1,  // Always 1.0 - we control size via fontSize
      scaleY: 1,  // Always 1.0 - we control size via fontSize
      angle: scaledPosition?.angle ?? savedPosition?.angle ?? 0,
      selectable: isInteractive,
      evented: isInteractive,
      hasControls: enableTextResizing,
      hasBorders: isInteractive,
      lockMovementX: !canMove,
      lockMovementY: !canMove,
      lockScalingX: !enableTextResizing,
      lockScalingY: !enableTextResizing,
      lockScalingFlip: true,
      cornerColor: '#F6DD5E',
      cornerStyle: 'rect',
      cornerSize: 6,
      transparentCorners: false,
      borderColor: '#F6DD5E',
      borderScaleFactor: 2,
      isText: true
    });

    // Hide middle controls (only keep corners for uniform scaling)
    textObj.setControlsVisibility({
      mt: false, // middle top
      mb: false, // middle bottom
      ml: false, // middle left
      mr: false  // middle right
    });

    // Clear font cache and recalculate dimensions for accurate bounding box
    fabric.util.clearFabricFontCache();
    textObj.initDimensions();
    textObj.setCoords();

    // POST-RENDER MEASUREMENT LOOP
    // Add to canvas FIRST (hidden), measure ACTUAL bounds, adjust until it fits
    // This guarantees text fits because we measure what's actually rendered
    const maxWidth = bounds.width - TEXT_MARGIN;
    const maxHeight = bounds.height - TEXT_MARGIN;

    // Use PIXEL SCANNING to find fontSize that actually fits
    // This measures real rendered pixels, not font metrics - 100% accurate
    let currentFontSize = fontSize;
    let iterations = 0;
    const MAX_ITERATIONS = 50;

    while (iterations < MAX_ITERATIONS && currentFontSize > MIN_FONT_SIZE) {
      // Measure ACTUAL pixel bounds using Fabric.js rendering
      const pixelBounds = measureTextPixelBounds(text, currentFontSize, fontFamily);

      if (pixelBounds.width <= maxWidth && pixelBounds.height <= maxHeight) {
        break;  // Fits!
      }

      // Reduce fontSize - adaptive steps for speed
      const reduction = currentFontSize > 50 ? 5 : (currentFontSize > 20 ? 2 : 1);
      currentFontSize -= reduction;
      iterations++;
    }

    currentFontSize = Math.max(currentFontSize, MIN_FONT_SIZE);

    // Apply the calculated fontSize to the text object
    if (currentFontSize !== fontSize) {
      textObj.set({ fontSize: currentFontSize });
      textObj.initDimensions();
      textObj.setCoords();

      // Only update position if one already exists (user has moved/resized text)
      // Otherwise just track fontSize without creating a partial position
      if (savedPosition && savedPosition.left != null) {
        locState.personalization.position = {
          ...savedPosition,
          fontSize: currentFontSize
        };
      }
    }

    // Add to canvas and render ONCE (no flicker)
    state.fabricCanvas.add(textObj);

    // NOW render - only once, no flicker
    state.fabricCanvas.renderAll();
  }

  function clearFabricText() {
    if (!state.fabricCanvas) return;

    // Remove only text objects, keep logo and bounding box
    const objects = state.fabricCanvas.getObjects();
    objects.forEach(obj => {
      if (obj.isText) {
        state.fabricCanvas.remove(obj);
      }
    });
  }

  function handleTextModified(e) {
    const obj = e.target;
    if (!obj || !obj.isText) return;

    const location = getActiveLocation();
    if (!location) return;

    const locState = state.locationState[location.id];

    // Save text position with fontSize and canvas dimensions for screenshot scaling
    locState.personalization.position = {
      left: obj.left,
      top: obj.top,
      fontSize: obj.fontSize,
      angle: obj.angle,
      canvasWidth: state.fabricCanvas.width,
      canvasHeight: state.fabricCanvas.height
    };
  }

  /**
   * Apply thread colors using logo system universal pattern
   * SVG uses placeholder fills like #1e, #2e, #3e etc.
   * threadInstructions format: [[hex, name, code], [hex, name, code], ...]
   * or from metafield: [{hex, name, code}, {hex, name, code}, ...]
   * customColors: optional object { [index]: { hex, name, code } } to override default colors
   * threadLayers: optional array of parsed thread layer info (for fixed hex color support)
   */
  function applyThreadColors(svgText, threadInstructions, switchy, customColors = {}, threadLayers = null) {
    if (!svgText) return svgText;

    // Parse the SVG
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');

    // Find all <g> tags with fill attributes
    const gTags = doc.querySelectorAll('g[fill]');

    gTags.forEach((gTag, gTagIdx) => {
      const fillValue = gTag.getAttribute('fill');

      // SWITCHY LOGIC: Handle special hex code #F5D76E
      if (fillValue && fillValue.toLowerCase() === '#f5d76e') {
        if (switchy === 'darkColor') {
          const darkColor = gTag.getAttribute('darkColor');
          if (darkColor) {
            const newHex = darkColor.startsWith('#') ? darkColor : `#${darkColor}`;
            gTag.setAttribute('fill', newHex);
            return;
          }
        } else if (switchy === 'lightColor') {
          const lightColor = gTag.getAttribute('lightColor');
          if (lightColor) {
            const newHex = lightColor.startsWith('#') ? lightColor : `#${lightColor}`;
            gTag.setAttribute('fill', newHex);
            return;
          }
        }
      }

      // POSITION-BASED MATCHING: When threadLayers is available, match each <g> tag
      // to its unique layer by position (gTagForEachIndex) — handles both switchable and fixed hex
      if (threadLayers) {
        const layer = threadLayers.find(l => l.gTagForEachIndex === gTagIdx);
        if (layer) {
          // Check for custom color override first (each layer has a unique index)
          if (customColors[layer.index]?.hex) {
            const customHex = customColors[layer.index].hex;
            const newHex = customHex.startsWith('#') ? customHex : `#${customHex}`;
            gTag.setAttribute('fill', newHex);
            return;
          }

          // For switchable threads, fall back to default from threadInstructions
          if (!layer.isFixedHex && threadInstructions && Array.isArray(threadInstructions)) {
            const threadData = threadInstructions[layer.originalThreadIndex];
            if (threadData) {
              let hex;
              if (Array.isArray(threadData)) {
                hex = threadData[0];
              } else if (threadData.hex) {
                hex = threadData.hex;
              }
              if (hex) {
                const newHex = hex.startsWith('#') ? hex : `#${hex}`;
                gTag.setAttribute('fill', newHex);
                return;
              }
            }
          }

          // For editable fixed hex, use the default hex from thread library
          if (layer.isFixedHex && layer.isEditable) {
            const newHex = layer.defaultHex.startsWith('#') ? layer.defaultHex : `#${layer.defaultHex}`;
            gTag.setAttribute('fill', newHex);
            return;
          }

          // Non-editable fixed hex: leave original color unchanged
          return;
        }
      }

      // FALLBACK: No threadLayers or no matching layer — use original logic.
      // Placeholder detection is now an exact lookup against the 5 marker
      // hexes in SWITCHABLE_PLACEHOLDER_SLOTS (see top of module).
      const fallbackSlotIdx = getSwitchablePlaceholderSlot(fillValue);

      if (fallbackSlotIdx !== undefined && threadInstructions && Array.isArray(threadInstructions)) {
        const threadIndex = fallbackSlotIdx;

        if (customColors[threadIndex]?.hex) {
          const customHex = customColors[threadIndex].hex;
          const newHex = customHex.startsWith('#') ? customHex : `#${customHex}`;
          gTag.setAttribute('fill', newHex);
          return;
        }

        if (threadInstructions[threadIndex]) {
          const threadData = threadInstructions[threadIndex];
          let hex;
          if (Array.isArray(threadData)) {
            hex = threadData[0];
          } else if (threadData.hex) {
            hex = threadData.hex;
          }
          if (hex) {
            const newHex = hex.startsWith('#') ? hex : `#${hex}`;
            gTag.setAttribute('fill', newHex);
          }
        }
      }
    });

    // Serialize back to string
    const serializer = new XMLSerializer();
    return serializer.serializeToString(doc);
  }

  // ============================================
  // CANVAS / BOUNDING BOX
  // ============================================
  function parseCoords(coordString) {
    // Parse "x_y_width_height" format
    if (!coordString) return null;
    const parts = coordString.split('_').map(Number);
    if (parts.length !== 4 || parts.some(isNaN)) return null;
    return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
  }

  /**
   * Capture just the product image (no overlays) as a JPEG Blob.
   * Used when no locations are selected.
   */
  async function captureProductImageOnly() {
    const img = elements.productImage;
    if (!img || !img.complete || !img.naturalWidth) {
      return null;
    }

    // Output dimensions: scale display size down to 800px max (cart thumbnail)
    const dispW = img.clientWidth || img.naturalWidth;
    const dispH = img.clientHeight || img.naturalHeight;
    const scale = Math.min(1, 800 / Math.max(dispW, dispH));
    const outW = Math.round(dispW * scale);
    const outH = Math.round(dispH * scale);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');

    try {
      ctx.drawImage(img, 0, 0, outW, outH);
    } catch (e) {
      console.error('[Screenshot] captureProductImageOnly drawImage failed (CORS?):', e);
      return null;
    }

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        if (!blob) console.warn('[Screenshot] captureProductImageOnly toBlob returned null');
        resolve(blob);
      }, 'image/jpeg', 0.85);
    });
  }

  function clearCanvas() {
    if (!state.fabricCanvas) return;
    state.fabricCanvas.clear();
  }

  function clearBoundingBox() {
    if (!state.fabricCanvas) return;

    // Collect first, then remove — mutating during forEach skips elements
    const toRemove = state.fabricCanvas.getObjects().filter(obj => obj.isBoundingBox);
    toRemove.forEach(obj => state.fabricCanvas.remove(obj));
  }

  function renderBoundingBox(location) {
    if (!state.fabricCanvas) return;

    const img = elements.productImage;

    // Get the displayed image dimensions
    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    // Resize Fabric canvas to match displayed image
    state.fabricCanvas.setWidth(displayWidth);
    state.fabricCanvas.setHeight(displayHeight);

    // Clear existing bounding boxes
    clearBoundingBox();

    // Don't render bounding box if no locations are selected
    // (location may be displayed for preview but not selected for customization)
    if (state.selectedLocations.length === 0) {
      return;
    }

    // Don't render bounding box if this location is not selected
    if (!state.selectedLocations.includes(location.id)) {
      return;
    }

    // Parse bounding box coordinates (based on original image size, typically 2048px)
    const canvasCoords = parseCoords(location.canvas) || parseCoords(location.defaultPlacement);
    if (!canvasCoords) {
      return;
    }

    // Original image dimensions (from the image_url filter)
    const originalWidth = img.naturalWidth || 2048;
    const originalHeight = img.naturalHeight || 2048;

    // Calculate scale factor
    const scaleX = displayWidth / originalWidth;
    const scaleY = displayHeight / originalHeight;

    // Scale the bounding box coordinates
    const scaledBox = {
      x: canvasCoords.x * scaleX,
      y: canvasCoords.y * scaleY,
      width: canvasCoords.width * scaleX,
      height: canvasCoords.height * scaleY
    };

    // Draw bounding box (dashed blue rectangle)
    const boundingRect = new fabric.Rect({
      left: scaledBox.x,
      top: scaledBox.y,
      width: scaledBox.width,
      height: scaledBox.height,
      fill: 'transparent',
      stroke: 'rgba(246, 221, 94, 0.5)',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      rx: 4,
      ry: 4,
      selectable: false,
      evented: false,
      isBoundingBox: true
    });

    state.fabricCanvas.add(boundingRect);
    state.fabricCanvas.sendToBack(boundingRect);
  }

  // ============================================
  // HELPERS
  // ============================================
  function getActiveLocation() {
    if (!state.activeLocation) return null;
    return state.locations.find(l => l.id === state.activeLocation) || null;
  }

  /**
   * Render the logo for the currently active location
   * Called when switching locations to restore/clear the logo overlay
   */
  function renderActiveLocationLogo() {
    const location = getActiveLocation();

    // Always clear existing logo from Fabric canvas first
    clearFabricLogo();

    if (!location) return;

    // Don't render if no locations are selected for customization
    if (state.selectedLocations.length === 0) return;

    // Don't render if this location is not selected
    if (!state.selectedLocations.includes(location.id)) return;

    const locState = state.locationState[location.id];
    if (!locState?.selectedLogo) {
      // No logo selected for this location - already cleared above
      return;
    }

    // Render the logo at saved position (or default if no position saved)
    renderLogoOnCanvas(location, locState.selectedLogo.id, locState.selectedLogo.url);
  }

  // ============================================
  // INIT ON DOM READY
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================
  // EXPOSE PUBLIC API
  // ============================================
  // Expose functions for external use (e.g., modal controller)
  window.CustomizerV2 = {
    initInModal: initInModal,
    restoreProductPageContext: restoreProductPageContext,
    captureAndUploadScreenshots: captureAndUploadScreenshots
  };

})();
